import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getMyBattleMonster,
  getPlayerProgress,
  getZoneStages,
  getBattleEnemyImages,
  getAllZones,
  getEnemyMovePool,
  type ZoneListEntry,
} from "@/lib/game/battle-v2/loaders.functions";
import { settleStage } from "@/lib/game/battle-v2/settle.functions";
import { useFrontierAutoCast } from "@/lib/dev/frontier-autocast";
import { useDevBattleIntervalMs } from "@/lib/dev/speed";
import { ACHIEVEMENT_MAP, achievementTitle } from "@/lib/game/achievements";
import { getActiveExpedition } from "@/lib/game/expedition.functions";
import { listItems } from "@/lib/game/items.functions";
import { useConsumable } from "@/lib/game/shop.functions";
import { getMyMonster } from "@/lib/game/monster.functions";
import { makeBossCatalog, slugCatalog } from "@/lib/game/battle-v2/slug-catalog";
import { resolveStageEncounter } from "@/lib/game/combat-core/stages";
import { GEGNER_AUSRUESTUNG } from "@/lib/game/battle-v2/enemy-gear";
import { ENEMY_REAL_MOVE_CD_MULT } from "@/lib/game/combat-core/stages/encounter";
import { KILL_HEAL_PCT } from "@/lib/game/combat-core/simulate";
import { COMBO_DMG_PER_STACK, COMBO_MAX } from "@/lib/game/combat-core/damage";
import { makeEnemyMoveBuilder } from "@/lib/game/battle-v2/enemy-moves";
import { INTRINSIC_BASIC_COOLDOWN_MS, INTRINSIC_BASIC_POWER } from "@/lib/game/battle-v2/snapshot";
import { DOT_NEUTRAL, dotBezeichnung } from "@/lib/game/status-worte";
import { SLOT_UNLOCK, MAX_MOVE_SLOTS, listMonsterMoves } from "@/lib/game/moves.functions";
import type {
  StageScript,
  MoveSnapshot,
  InputLogEntry,
  StatusFlag,
} from "@/lib/game/combat-core/types";
import {
  useLiveCombat,
  type LiveCastPhase,
} from "@/components/game/battle-v2/useLiveCombat";
import {
  BattleEnemyCard,
  BattlePlayerCard,
  EnemyCastRow,
  QueuedEnemyCard,
} from "@/components/game/battle-v2/BattleCards";
import { castProgressPct } from "@/components/game/battle-v2/cast-phase";
import { MoveTile } from "@/components/game/battle-v2/MoveTile";
import { HASH_VERSION, STAGE_MAX_DURATION_MS, warZeitablauf } from "@/lib/game/combat-core";
import { getKernelVersion } from "@/lib/game/battle-v2/loaders.functions";
import {
  typVorteilFuerAnzeige,
  typVorteilSatz,
  type TypVorteil,
} from "@/lib/game/typ-vorteil-anzeige";
import {
  FloatNumLayer,
  Sticker,
  useHitFloaters,
} from "@/components/game/battle-v2/BattleMoments";
import { TypeBadge, type EnemyTier } from "@/components/game/Badges";
import type { CreatureType } from "@/lib/game/types";
import { setKampfOrt } from "@/lib/feedback/kontext";
import {
  ChevronDown,
  Coins,
  Crown,
  Flag,
  FlaskConical,
  Hand,
  Info,
  ScrollText,
  Sparkles,
} from "lucide-react";
import { Panel } from "@/components/game/Panel";
import {
  FilterPills,
  ItemIcon,
  MicroLabel,
  type Rarity,
} from "@/components/game/ScreenKit";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { catalogEntryBySlug } from "@/lib/game/enemy-catalog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InfoTip } from "@/components/game/InfoTip";
// Bewusst KEIN Radix-Tooltip mehr: dessen Trigger hing hier per `asChild` an
// den Slot-Buttons, und ein `disabled` Button feuert keine Pointer-Events —
// die Blase ging weder per Maus noch per Tap auf, ausgerechnet auf den
// gesperrten/ladenden Kacheln, deren Erklärung man braucht. Alle Erklärungen
// laufen jetzt über die projekteigene InfoTip-Popover-Komponente (siehe dort).
import { useTutorial, useTutorialCtx } from "@/components/game/TutorialProvider";
import { MoveLearnedModal, type LearnedMoveInfo } from "@/components/game/MoveLearnedModal";
import { LootReveal } from "@/components/game/battle-v2/LootReveal";
import { toast } from "sonner";


/**
 * Rechnet der Server dieselbe Kernel-Version wie dieser Tab?
 *
 * Nach einer Veröffentlichung hält ein offener Tab noch das alte Bündel. Er
 * rechnet dann einen anderen Kampf als der Server — und der Settle lehnt einen
 * abweichenden AUSGANG mit 409 ab, der ausgefochtene Kampf ist ersatzlos weg.
 * Weil dieses Paket die Gegner stärker macht, trifft das überdurchschnittlich
 * oft ausgerechnet gewonnene Kämpfe.
 *
 * ⚠️ Bewusst OHNE die gemeinsamen Abfrage-Optionen: Die anderen halten ihre
 * Daten 60 Sekunden und laden weder bei Fokus noch nach einem
 * Verbindungsabriss nach. Genau das wäre hier fatal — die Prüfung wäre so
 * lange blind, wie der Cache alt ist, und das ist die Zeitspanne, in der
 * Kämpfe verloren gehen.
 *
 * ⚠️ Schlägt die Abfrage fehl (offline, Server nicht erreichbar), bleibt
 * das Ergebnis leer und die Sperre greift NICHT. Das ist Absicht: Eine
 * Prüfung, die bei jedem Netz-Schluckauf das Spiel blockiert, wird
 * abgeschaltet — und schützt dann gar nichts mehr.
 */
function useVeralteteKernelVersion(): boolean {
  const q = useQuery({
    queryKey: ["kernel-version"],
    queryFn: () => getKernelVersion(),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return q.data !== undefined && q.data.version !== HASH_VERSION;
}

/** Lädt die Seite neu und umgeht dabei den Bündel-Cache des Browsers. */
function seiteNeuLaden(): void {
  if (typeof window !== "undefined") window.location.reload();
}

export const Route = createFileRoute("/_app/game/battle")({
  component: BattlePage,
  head: () => ({
    meta: [
      { title: "Battle – Xenomons" },
      { name: "description", content: "Fight endless AI opponents in neon zones with your Xenomon. Auto-combat, moves and loot drops." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Xenomons Battle – Fight Cyber Creatures" },
      { property: "og:description", content: "Endless auto-combat against AI opponents across progressively harder cyber zones." },
    ],
  }),
});

const AUTO_ADVANCE_KEY = "battle-v2:auto-advance";
const AUTO_CAST_KEY = "battle-v2:auto-cast";
const BATTLE_V2_ACTIVE_KEY = "battle-v2:active-lease";

function readBoolStore(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(key);
  if (v === "1") return true;
  if (v === "0") return false;
  return fallback;
}
function writeBoolStore(key: string, v: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, v ? "1" : "0");
}

function randomSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff) + 1;
}

// Spiegel der serverseitigen Ableitung in settle.functions.ts: jeder gezählte
// manuelle Cast gibt 5 Punkte Aktiv-Score, 100 Punkte = +15 % XP/Gold. Nur
// Anzeige — der Server rechnet den Bonus selbst aus dem inputLog.
const ACTIVE_SCORE_PER_CAST = 5;
const ACTIVE_BONUS_MAX_PCT = 15;
const ACTIVE_CASTS_FOR_CAP = 100 / ACTIVE_SCORE_PER_CAST;
const ACTIVE_BONUS_PCT_PER_CAST = ACTIVE_BONUS_MAX_PCT / ACTIVE_CASTS_FOR_CAP;

function activeBonusPctFor(casts: number): number {
  return (Math.min(100, casts * ACTIVE_SCORE_PER_CAST) / 100) * ACTIVE_BONUS_MAX_PCT;
}

/**
 * "0.75" → "0,75". Einziger Dezimal-Formatter dieses Screens — Erklärtexte,
 * Live-Zahlen, Toast und Ergebnis-Zeile standen sonst mit unterschiedlichen
 * Trennern direkt untereinander.
 */
function de(n: number, digits: number): string {
  return n.toFixed(digits).replace(".", ",");
}

/**
 * Zählt die Casts nach DERSELBEN Regel wie `settle.functions.ts`: ein Eintrag
 * zählt nur, wenn seit dem letzten gewerteten Eintrag desselben Moves
 * mindestens ein voller Cooldown vergangen ist. Doppel-Taps im selben 100-ms-
 * Tick landen beide im inputLog (`useLiveCombat.cast` prüft nur den aktuellen
 * Cooldown, der erst im nächsten Tick gesetzt wird), werden serverseitig aber
 * nur einmal gewertet. Ohne diese Angleichung verspricht die Live-Anzeige mehr
 * XP/Gold, als der Sieg-Toast danach meldet.
 *
 * `inputLog` ist chronologisch (jeder Eintrag bekommt `state.now`), daher ohne
 * Sortierung — der Server sortiert nur defensiv.
 *
 * Der Mindestabstand ist der volle Zyklus `castMs + cooldownMs`, NICHT der
 * Cooldown allein — seit der Cast-Phase (v8) läuft der Cooldown erst ab dem
 * Treffer, und `settle.functions.ts` prüft exakt gegen diese Summe. Die
 * Abstände liefert der Aufrufer fertig (`playerMoveCds`), damit hier keine
 * zweite Balance-Rechnung entsteht.
 */
function countScoredCasts(
  inputLog: readonly InputLogEntry[],
  gapMsByMoveId: Map<string, number>,
): number {
  const lastCountedAt = new Map<string, number>();
  let counted = 0;
  for (const entry of inputLog) {
    const gapMs = gapMsByMoveId.get(entry.moveId);
    if (gapMs === undefined) continue;
    const last = lastCountedAt.get(entry.moveId);
    if (last !== undefined && entry.tMs - last < gapMs) continue;
    lastCountedAt.set(entry.moveId, entry.tMs);
    counted += 1;
  }
  return counted;
}

/**
 * Obergrenze der gewerteten Casts nach `ms` Kampfzeit — die Zahl, gegen die
 * die Aktiv-Bonus-Anzeige ehrlich sein muss.
 *
 * Rechnung: Jede Attacke ist bei t=0 bereit (das Windup ist entfallen) und
 * danach alle `castMs + cooldownMs` erneut — dieselbe Summe, gegen die der
 * Server wertet. Mehr als `1 + floor(ms / Zyklus)` gewertete Einträge kann
 * ein Move also nicht liefern, egal wie oft getippt wird.
 *
 * Der intrinsische Grundangriff steht bewusst NICHT drin, obwohl der Server
 * ihn zählen würde: Er trägt `castMs = 0` und wird vom Auto-Cast in genau dem
 * Tick gestartet, in dem sein Cooldown 0 erreicht (`pickAutoMove` läuft in
 * jedem Tick, auch bei ausgeschaltetem Auto-Cast — dort ist er der einzige
 * Kandidat). Seine Kachel ist `disabled={intrinsicCdMs > 0}` und wird damit
 * praktisch nie im bereiten Zustand gerendert; automatisch gefeuerte Casts
 * schreiben keinen inputLog-Eintrag. Ihn mitzuzählen hieße, dem Spieler Casts
 * zu versprechen, die er nicht auslösen kann.
 */
function maxScoredCastsWithin(moves: readonly MoveSnapshot[], ms: number): number {
  let n = 0;
  for (const m of moves) {
    const gapMs = castMsOf(m) + m.cooldownMs;
    if (gapMs <= 0) continue;
    n += 1 + Math.floor(ms / gapMs);
  }
  return n;
}

/**
 * Frühester Zeitpunkt (ms), an dem das Move-Set den Bonus-Deckel erreichen
 * KANN — `null`, wenn das innerhalb von `limitMs` nicht geht. `limitMs` ist
 * die `maxDurationMs` der Stage: Danach endet der Kampf, ein späterer Cast
 * existiert nicht mehr.
 */
function msToActiveBonusCap(moves: readonly MoveSnapshot[], limitMs: number): number | null {
  if (moves.length === 0) return null;
  const stepMs = 500;
  for (let t = 0; t <= limitMs; t += stepMs) {
    if (maxScoredCastsWithin(moves, t) >= ACTIVE_CASTS_FOR_CAP) return t;
  }
  return null;
}

function moveDisplayName(move: MoveSnapshot): string {
  return move.name ?? move.id;
}

/**
 * Cast-Zeit eines Moves in ms. Steht fertig im Snapshot — `buildMonsterSnapshot`
 * bzw. `buildEnemyMoves` leiten sie über `castMsForMove` (combat-core/cast.ts)
 * ab, damit Client und Server denselben hash-relevanten Wert einsetzen. Hier
 * wird sie deshalb gelesen, nie nachgerechnet.
 */
function castMsOf(move: MoveSnapshot): number {
  return move.castMs ?? 0;
}

/**
 * Kampfzyklus eines Moves als Text: Ausholen bis zum Treffer, danach
 * Abklingzeit. `cooldownMs` ist bereits der gekoppelte Wert NACH dem Treffer
 * (C2-A, `effectiveCooldownMs`), die Summe also die echte Zeit zwischen zwei
 * Treffern.
 *
 * Bewusst wird nur diese Summe genannt und NICHT zusätzlich der rohe
 * Katalog-Cooldown: bis zur Kopplungsgrenze frisst das Ausholen den Cooldown
 * bereits auf, bei längeren kommt es obendrauf — zwei Zahlen nebeneinander
 * hat der Spieler zwangsläufig falsch addiert.
 */
function castCycleText(move: MoveSnapshot): string {
  const castMs = castMsOf(move);
  const cdMs = move.cooldownMs;
  if (castMs === 0) {
    return `Ohne Ausholen: trifft sofort, danach ${de(cdMs / 1000, 1)}s Abklingzeit — ein Treffer alle ${de(cdMs / 1000, 1)}s.`;
  }
  return (
    `Zyklus: ${de(castMs / 1000, 1)}s ausholen → Treffer → ${de(cdMs / 1000, 1)}s Abklingzeit. ` +
    `Ein Treffer alle ${de((castMs + cdMs) / 1000, 1)}s.`
  );
}

/** Kurzform desselben Zyklus für enge Listenzeilen (Monster-Info-Panel). */
function castCycleShort(move: MoveSnapshot): string {
  const castMs = castMsOf(move);
  const cycleS = (castMs + move.cooldownMs) / 1000;
  if (castMs === 0) return `Treffer alle ${de(cycleS, 1)}s`;
  return `${de(castMs / 1000, 1)}s ausholen · Treffer alle ${de(cycleS, 1)}s`;
}

/** Eigenständiger ⓘ-Trigger — mind. 24px Tap-Ziel neben Labels/Zeilen. */
// Dieselbe Grundform wie `InfoToggle` weiter unten: 2-px-Ink-Outline auf
// Sekundärfläche. Der frühere 1-px-Rahmen in Border-Transparenz stammte aus
// der Neon-Sprache und stand nach dem Kampf-Umbau als einziges Element dieser
// Art zwischen lauter Ink-Outlines.
const INFO_DOT_CLASS =
  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border-2 border-ink bg-secondary text-[11px] leading-none text-muted-foreground";

const COMBO_MAX_BONUS_PCT = Math.round(COMBO_MAX * COMBO_DMG_PER_STACK * 100);
const COMBO_TIP = `Combo: +${Math.round(COMBO_DMG_PER_STACK * 100)} % Schaden pro Stack, maximal ${COMBO_MAX} Stacks (+${COMBO_MAX_BONUS_PCT} %). Jeder eigene Treffer baut einen Stack auf. Sobald dein Xenomon selbst getroffen wird, fällt die Combo auf 0. Kein Zeitverfall.`;

/**
 * Kein Versprechen über „oft freie" Kacheln mehr: `pickAutoMove` nimmt pro Tick
 * den ersten bereiten Move und fällt sonst auf den Basis-Angriff zurück — der
 * ist damit der meistgefeuerte, nicht der freieste.
 *
 * Die frühere Messung („nur ein kleiner einstelliger Prozentsatz der Ticks
 * antippbar") stammt aus der C1-A-Zeit, als ein laufender Cast die ganze Leiste
 * sperrte, und wird deshalb nicht mehr behauptet — seit C1-B sperrt nur noch
 * der Move sich selbst. Zahlen bleiben aus dem Text: sie hängen am Move-Set.
 */
const AUTO_CAST_TIP =
  "Dein Xenomon holt immer nur mit EINER Attacke gleichzeitig aus — wer ausholt, fängt nichts Neues an. Der eingebaute Basis-Angriff ist davon ausgenommen und feuert nebenher weiter, du stehst also nie wehrlos da. An: Der Automat feuert Slot 1 zuerst, sonst die nächste bereite Attacke. Selbst antippen kannst du jede Attacke, deren Abklingzeit vorbei ist — aber nur, solange keine andere ausholt. Aus: Deine gelernten Attacken tippst du selbst an; der Basis-Angriff läuft weiter von allein. Umschalten wirkt auch mitten im Kampf sofort. Auf einer neuen Stage (Frontier) ist der Schalter fest aus — dort gilt die Regel für alle gleich, und der Server rechnet den Kampf genau so nach.";

/**
 * Der Kampfzyklus in einem Satz. Bewusst ohne feste Sekundenzahlen — die
 * Ausholzeit hängt am Move (Power-Bänder in combat-core/cast.ts) und steht
 * konkret auf jeder Slot-Kachel.
 *
 * Die Richtung der Regel steht ausdrücklich drin: `castMsForMove` gibt
 * SCHWACHEN Attacken die längste Ausholzeit (power < 0,9 → 4s) und wuchtigen
 * die kürzeste (power ≥ 1,8 → 1s). Ein „je nach Wucht" liest sich zwangsläufig
 * andersherum und war schlicht falsch.
 */
const CAST_TIP =
  "Ausholen: Jede Attacke ist ab Kampfstart sofort verfügbar, holt dann aber aus, bevor sie trifft — die Kachel färbt sich und zeigt „holt aus“ mit der Restzeit. Der Treffer fällt am Ende des Ausholens, erst danach läuft die Abklingzeit. Wie lange ausgeholt wird, hängt an der Attacke — und zwar umgekehrt zur Wucht: Leichte Dauerfeuer-Attacken holen am längsten aus, die wuchtigsten am kürzesten; die Ultimate hat als Einzige eine feste, lange Ausholzeit. Jede Attacke holt für sich aus: Mehrere Kacheln können gleichzeitig ausholen, jede läuft ihren eigenen Zyklus. Nur dieselbe Attacke kann nicht doppelt ausholen — ihre Kachel bleibt gesperrt, bis ihr Schlag gelandet ist; alle anderen bleiben antippbar. Ein begonnener Schlag landet — selbst abbrechen kannst du ihn nicht, und ein gewöhnlicher Treffer stört ihn auch nicht. Manche Attacken reißen ein Ziel aber aus dem Ausholen: Alles, was dort gerade unterwegs war, verpufft, und die Abklingzeiten laufen trotzdem an. Das gilt für beide Seiten. Auf einer neuen Stage startest du jedes Ausholen selbst per Tap; nur der Basis-Angriff (ohne Ausholen) feuert dort von allein. Gegner mit eigenen Attacken holen genauso aus, auch mehrere gleichzeitig — die Gegnerkarte zeigt, was als Nächstes einschlägt, und wie viele weitere unterwegs sind.";

/**
 * Freischaltstufen kommen aus SLOT_UNLOCK (moves.functions.ts) — Zahlen hier
 * nicht duplizieren — der Text lag schon einmal daneben (Slot 4 ab Lv 7 und
 * Slot 5 ab Lv 12 fehlten komplett).
 */
const SLOTS_TIP = (() => {
  const staged = Object.entries(SLOT_UNLOCK)
    .filter(([, level]) => level > 1)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([slot, level]) => `Slot ${slot} ab Lv ${level}`)
    .join(", ");
  return (
    `Slots 1–${MAX_MOVE_SLOTS} sind deine Moves mit Cooldown, Slot ${MAX_MOVE_SLOTS} ist der Ultimate. ` +
    `Freigeschaltet wird nach Level: ${staged}; Slots 1–3 hast du von Anfang an. ` +
    "Die Kachel „Basis“ davor ist der fest eingebaute Basis-Angriff — nicht belegbar, antippbar immer dann, wenn sein eigener Cooldown abgelaufen ist; er holt nie aus, und das Ausholen anderer Attacken sperrt ihn nicht."
  );
})();

/** Kurzfassung des Aktiv-Bonus (geschützte Leerzeichen vor dem Prozent). */
const NBSP = " ";
const ACTIVE_BONUS_SUMMARY =
  `(+${de(ACTIVE_BONUS_PCT_PER_CAST, 2)}${NBSP}% XP/Gold pro gewertetem Cast,` +
  ` maximal +${ACTIVE_BONUS_MAX_PCT}${NBSP}% bei ${ACTIVE_CASTS_FOR_CAP} Casts)`;

const ACTIVE_BONUS_TIP =
  `Jeder selbst getippte Cast gibt +${de(ACTIVE_BONUS_PCT_PER_CAST, 2)} % XP und Gold, bei ${ACTIVE_CASTS_FOR_CAP} gewerteten Casts ist mit +${ACTIVE_BONUS_MAX_PCT} % Schluss. ` +
  "Gezählt werden nur Taps auf deine Slot-Attacken. Der eingebaute Basis-Angriff feuert immer von allein, sobald er bereit ist — automatisch gefeuerte Angriffe zählen nie, seine Kachel ist deshalb praktisch nie antippbar. " +
  "Und ein Tap zählt erst wieder, wenn seit dem letzten gewerteten Cast DERSELBEN Attacke ein voller Zyklus vergangen ist (Ausholen + Abklingzeit) — mehrfaches Tippen dazwischen bringt nichts. " +
  "Solange eine Attacke ausholt, ist die ganze Leiste gesperrt — dein Xenomon holt nur mit einer gleichzeitig aus. Der Basis-Angriff laeuft davon unberuehrt weiter. " +
  "Daraus folgt die Obergrenze: Mit wenigen Attacken ist der Höchstwert in einem normalen Kampf nicht zu schaffen — unter der Anzeige steht, was mit deinem Set drin ist. " +
  "Im Training kommt dir der Auto-Cast zusätzlich zuvor; auf einer neuen Stage (Frontier) ist er aus, dort geht jeder Cast auf dein Konto.";

/**
 * Die Frontier-Regel in Spielersprache (Stefan, 02.09.2026). Alle Zahlen aus
 * echten Konstanten: Der eingebaute Basis-Angriff trägt Power
 * `INTRINSIC_BASIC_POWER` und `INTRINSIC_BASIC_COOLDOWN_MS` (snapshot.ts) und
 * hat fest castMs 0 — er holt also nicht aus, sondern trifft sofort.
 *
 * Der Server setzt dieselbe Regel beim Nachrechnen durch (settle.functions.ts:
 * `autoCastEnabled = isFrontier ? false : data.autoCastEnabled`, Auto-Cast-
 * Toggles werden aus dem inputLog gestrippt). Der Client MUSS sie deshalb
 * exakt so anwenden, sonst weicht der Hash ab und der Sieg wird abgelehnt.
 *
 * ⚠️ ANGEZEIGT wird das alles nur, wenn der Kampf-Snapshot mindestens eine
 * NICHT-intrinsische Attacke trägt. Ein frisches Xenomon startet mit null
 * Katalog-Moves (die ersten kommen über das Level-Lernen, Slots 1–3 mit den
 * Level-Ups 2/3/4); dort liefern `pickAutoMove(player)` und
 * `pickAutoMove(player, isIntrinsicMove)` denselben Move, die Frontier-Regel
 * ist ein No-Op und es gibt schlicht nichts zu tippen. Ein Hinweis „Du
 * greifst selbst an" wäre ausgerechnet auf den Onboarding-Stages falsch.
 * Die MECHANIK (frozenAutoCast, disabled-Checkbox, Settle-Payload) hängt
 * weiterhin allein an `isFrontier` — nur die Ansage hängt am Move-Set.
 */
const FRONTIER_MANUAL_HEADLINE = "Neue Stage: Du greifst selbst an.";
const FRONTIER_MANUAL_BODY =
  "Auf neuen Stages kämpfst du selbst — im Training läuft es automatisch. " +
  `Dein Basis-Angriff feuert weiter von allein (Power ×${de(INTRINSIC_BASIC_POWER, 2)}, ohne Ausholen, ` +
  `alle ${de(INTRINSIC_BASIC_COOLDOWN_MS / 1000, 1)}s), deine Slot-Attacken tippst du.`;
const FRONTIER_MANUAL_TIP =
  FRONTIER_MANUAL_BODY +
  " Der Server erzwingt die Regel beim Nachrechnen des Kampfes — sie ist also keine Deko, sondern gilt für alle gleich. " +
  `Jeder gewertete Cast bringt hier +${de(ACTIVE_BONUS_PCT_PER_CAST, 2)}${NBSP}% XP und Gold (Deckel +${ACTIVE_BONUS_MAX_PCT}${NBSP}% bei ${ACTIVE_CASTS_FOR_CAP} Casts) — ohne dass der Auto-Cast dir zuvorkommt. ` +
  "Der Deckel selbst ist damit noch lange nicht erreicht: Wie viele Casts dein Set überhaupt hergibt, steht unter der Aktiv-Bonus-Anzeige. " +
  "Zurück ins Training kommst du über die Stage-Auswahl — dort zählen XP und Gold 50 %.";

function BattlePage() {
  const loadMonster = useServerFn(getMyBattleMonster);
  const loadProgress = useServerFn(getPlayerProgress);
  const loadZone = useServerFn(getZoneStages);
  const loadAllZones = useServerFn(getAllZones);
  const loadMovePool = useServerFn(getEnemyMovePool);
  const loadMoveSlots = useServerFn(listMonsterMoves);

  // Erste-Schritte-Tutorials: lagen historisch auf /game/home (Battle v1);
  // seit dem Onboarding-Redirect ist /game/battle die Lande-Route.
  useTutorial("home_intro");
  useTutorial("zone_coach");
  useTutorial("nav_moves_coach");

  // Gürtel-und-Hosenträger neben useLiveCombat's Input-Freeze: während eines
  // Kampfs sollen Fokus/Reconnect keinen Refetch triggern, der Prop-Refs neu
  // erzeugt.
  const commonQueryOpts = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
  } as const;

  const veralteteVersion = useVeralteteKernelVersion();

  const monsterQ = useQuery({
    queryKey: ["battle-v2", "monster"],
    queryFn: () => loadMonster(),
    ...commonQueryOpts,
  });
  const progressQ = useQuery({
    queryKey: ["battle-v2", "progress"],
    queryFn: () => loadProgress(),
    ...commonQueryOpts,
  });
  const zonesQ = useQuery({
    queryKey: ["battle-v2", "zones"],
    queryFn: () => loadAllZones(),
    ...commonQueryOpts,
  });
  // Nur für die Slot-Kacheln: die echte `monster_moves.slot`-Nummer je Move.
  // Der Kampf-Snapshot trägt sie nicht (MoveSnapshot kennt nur `slot1`), und
  // `forgetMove` hinterlässt Lücken in der Slot-Reihe. Gleicher Query-Key wie
  // /game/moves → geteilter Cache statt eigener Runde, sobald der Move-Screen
  // einmal besucht (oder nach einem Swap/Vergessen invalidiert) wurde.
  const moveSlotsQ = useQuery({
    queryKey: ["monster-moves"],
    queryFn: () => loadMoveSlots(),
    ...commonQueryOpts,
  });
  const slotByMoveId = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of moveSlotsQ.data?.slots ?? []) {
      if (row?.move?.id) map.set(row.move.id, row.slot);
    }
    return map;
  }, [moveSlotsQ.data]);

  // Stage-Selector state — initialised from progress, updated on
  // frontier-advance so Auto-Weiter feels natural.
  const [selectedZone, setSelectedZone] = useState<number | null>(null);
  const [selectedStageIndex, setSelectedStageIndex] = useState<number | null>(null);
  // Beim Mounten (Navigation auf /game/battle) IMMER auf die Frontier
  // zurückspringen — vorher überlebte die manuelle Auswahl den Seitenwechsel
  // und Spieler landeten unbemerkt im Trainingsmodus.
  const initedFromProgressRef = useRef(false);
  useEffect(() => {
    if (initedFromProgressRef.current || !progressQ.data) return;
    initedFromProgressRef.current = true;
    setSelectedZone(progressQ.data.currentZone);
    setSelectedStageIndex(progressQ.data.currentStageIndex);
  }, [progressQ.data]);

  const jumpToFrontier = useCallback(() => {
    if (!progressQ.data) return;
    setSelectedZone(progressQ.data.currentZone);
    setSelectedStageIndex(progressQ.data.currentStageIndex);
  }, [progressQ.data]);

  const activeZoneId = selectedZone ?? progressQ.data?.currentZone ?? 1;
  const activeStageIndex = selectedStageIndex ?? progressQ.data?.currentStageIndex ?? 1;

  /**
   * Wo gekämpft wird, für eine Rückmeldung aus dem Spiel. Die Route allein
   * (`/game/battle`) sagt nicht, welcher Kampf gemeint war — und „Stage 7
   * fühlt sich zäh an" ist ohne die Nummer wertlos.
   *
   * ⚠️ Gemeldet wird NUR, wenn die Werte auch echt sind. `activeZoneId` fällt
   * auf `1` zurück, solange die Fortschritts-Abfrage läuft (am Handy im
   * Mobilfunknetz durchaus eine Sekunde) — und „Zone 1, Stage 1" ist ein
   * plausibler Wert, den später niemand anzweifelt, obwohl der Spieler auf
   * Zone 4 stand. Lieber gar kein Ort als ein erfundener: Das Feedback-Fenster
   * zeigt dann die Route, und die ist wenigstens wahr.
   */
  useEffect(() => {
    if (!progressQ.data && selectedZone === null) {
      setKampfOrt(null);
      return;
    }
    setKampfOrt({ zoneId: activeZoneId, stageIndex: activeStageIndex });
    return () => setKampfOrt(null);
  }, [activeZoneId, activeStageIndex, progressQ.data, selectedZone]);

  const zoneQ = useQuery({
    queryKey: ["battle-v2", "zone", activeZoneId],
    queryFn: () => loadZone({ data: { zoneId: activeZoneId } }),
    enabled: !!progressQ.data,
    ...commonQueryOpts,
  });

  const stageDef = zoneQ.data?.stages.find((s) => s.index === activeStageIndex) ?? null;

  const useRealMoves = zoneQ.data?.zone.useRealMoves === true;
  // Enemy move pool: loaded once when the current zone opts into real moves.
  // Cached long — the moves catalog barely changes at runtime, and settle
  // re-loads server-side anyway, so a stale pool here is not a hash risk.
  const enemyPoolQ = useQuery({
    queryKey: ["battle-v2", "enemy-move-pool"],
    queryFn: () => loadMovePool(),
    enabled: useRealMoves,
    staleTime: 15 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const bossCatalog = useMemo(
    () => makeBossCatalog(zoneQ.data?.boss ? [zoneQ.data.boss] : []),
    [zoneQ.data?.boss],
  );

  const enemyMoveBuilder = useMemo(() => {
    if (!useRealMoves || !enemyPoolQ.data) return undefined;
    return makeEnemyMoveBuilder(enemyPoolQ.data, ENEMY_REAL_MOVE_CD_MULT);
  }, [useRealMoves, enemyPoolQ.data]);

  const stageScript: StageScript | null = useMemo(() => {
    if (!zoneQ.data || !stageDef) return null;
    // Wait for the pool before building the encounter — otherwise the client
    // would run an AUTO_MOVE snapshot for one frame and the settle-hash would
    // diverge from the server's real-move simulation.
    if (useRealMoves && !enemyMoveBuilder) return null;
    const enemies = resolveStageEncounter(
      stageDef,
      zoneQ.data.zone,
      slugCatalog,
      bossCatalog,
      enemyMoveBuilder,
      // Getragene Ausruestung — dieselbe Funktion, die auch der Settle ruft.
      GEGNER_AUSRUESTUNG,
    );
    return {
      stageId: `z${activeZoneId}-s${activeStageIndex}`,
      enemies,
      maxDurationMs: STAGE_MAX_DURATION_MS,
      ignoreTypeMatrix: stageDef.ignoreTypeMatrix === true,
    };
  }, [zoneQ.data, stageDef, bossCatalog, activeZoneId, activeStageIndex, useRealMoves, enemyMoveBuilder]);

  // Ein frischer Seed je gemounteter Arena, neu gezogen bei „Weiter" und
  // „Erneut versuchen". `key={seed}` montiert LiveArena neu — das ist der
  // legitime Reset-Weg, im Unterschied zu einem Prop-Wechsel mitten im Kampf.
  //
  // Bis zum 09.09.2026 gab es daneben einen Knopf „Neuer Seed" im Kopfbereich.
  // Er war auf `import.meta.env.DEV` beschraenkt, auf der Live-Seite also nie
  // sichtbar — wohl aber in Lovables Vorschau, die ein Dev-Build ist, und dort
  // stand er mitten im Kampf-Screen, den Stefan und Sammy gerade beurteilen
  // sollten. Entfernt (Stefan, 09.09.2026). Wer denselben Kampf mit anderem
  // Zufall wiederholen will, verlaesst die Stage und betritt sie neu.
  const [seed, setSeed] = useState(randomSeed);
  const [inFight, setInFight] = useState(false);
  const [autoCast, setAutoCast] = useState(() => readBoolStore(AUTO_CAST_KEY, true));
  const [autoAdvance, setAutoAdvance] = useState(() => readBoolStore(AUTO_ADVANCE_KEY, false));
  // Offenes Lern-Angebot: Der Dialog wird von hier aus geöffnet (Badge), nicht
  // nur vom Settle — ein auf „Später entscheiden" geschobenes Angebot war
  // sonst unsichtbar UND blockierte jedes weitere (Regel „nur eines
  // gleichzeitig", level-up.ts).
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);

  useEffect(() => writeBoolStore(AUTO_CAST_KEY, autoCast), [autoCast]);
  useEffect(() => writeBoolStore(AUTO_ADVANCE_KEY, autoAdvance), [autoAdvance]);

  const queryClient = useQueryClient();
  const advance = useCallback(async () => {
    // Capture pre-refetch frontier so we can decide whether the selector
    // should hop forward after a frontier win.
    const prev = queryClient.getQueryData<{
      currentZone: number;
      currentStageIndex: number;
    }>(["battle-v2", "progress"]);
    const wasFrontier =
      !!prev &&
      prev.currentZone === activeZoneId &&
      prev.currentStageIndex === activeStageIndex;
    await queryClient.invalidateQueries({ queryKey: ["battle-v2"] });
    if (wasFrontier) {
      const next = queryClient.getQueryData<{
        currentZone: number;
        currentStageIndex: number;
      }>(["battle-v2", "progress"]);
      if (next) {
        setSelectedZone(next.currentZone);
        setSelectedStageIndex(next.currentStageIndex);
      }
    } else {
      // Training-Weiter: lokal Stage+1; am Zonenende in nächste (bereits
      // freigeschaltete) Zone hüpfen. settle wird dadurch NICHT verändert.
      const zoneEntry = zonesQ.data?.find((z) => z.id === activeZoneId);
      const stageCount = zoneEntry?.stageCount ?? activeStageIndex;
      if (activeStageIndex < stageCount) {
        setSelectedStageIndex(activeStageIndex + 1);
      } else {
        const prog = queryClient.getQueryData<{ highestZoneUnlocked: number }>([
          "battle-v2",
          "progress",
        ]);
        const cap = prog?.highestZoneUnlocked ?? activeZoneId;
        if (activeZoneId < cap) {
          setSelectedZone(activeZoneId + 1);
          setSelectedStageIndex(1);
        }
        // sonst: bleibt auf letzter Stage der Zone (Wiederholung).
      }
    }
    setSeed(randomSeed());
    setInFight(true);
  }, [queryClient, activeZoneId, activeStageIndex, zonesQ.data]);

  // On real-move zones (zone 1 among them) stageScript stays null until the
  // enemy pool has arrived, so wait for it here — otherwise the render right
  // after zoneQ resolves falls through to the "no stage data" branch below.
  const enemyPoolPending = useRealMoves && !enemyMoveBuilder && !enemyPoolQ.isError;
  if (monsterQ.isLoading || progressQ.isLoading || zoneQ.isLoading || enemyPoolPending) {
    return <div className="p-4 text-sm text-muted-foreground">Lade…</div>;
  }
  if (!monsterQ.data) {
    return <div className="p-4 text-sm">Kein aktives Xenomon gefunden.</div>;
  }
  if (enemyPoolQ.isError) {
    return (
      <div className="space-y-3 p-4 text-sm">
        <p>Die Attacken der Gegner konnten nicht geladen werden.</p>
        <Button size="sm" variant="outline" onClick={() => void enemyPoolQ.refetch()}>
          Erneut versuchen
        </Button>
      </div>
    );
  }
  if (!zoneQ.data || !stageDef || !stageScript || !progressQ.data) {
    return <div className="p-4 text-sm">Keine Stage-Daten.</div>;
  }

  const zone = zoneQ.data.zone;
  const progress = progressQ.data;
  const isFrontierSelection =
    activeZoneId === progress.currentZone && activeStageIndex === progress.currentStageIndex;

  // Hat der Spieler überhaupt etwas zu tippen? Abgeleitet aus dem ECHTEN
  // Kampf-Snapshot (derselbe, der gleich in den Kernel geht), nicht aus dem
  // Level geraten: Ein frisches Xenomon trägt nur den intrinsischen
  // Grundangriff, dort ist die Frontier-Regel ein No-Op (siehe
  // FRONTIER_MANUAL_*). Ohne Slot-Attacke wird sie deshalb nicht angekündigt —
  // und der Auto-Cast-Schalter verschwindet, weil beide Stellungen exakt
  // denselben Kampf ergeben (`pickAutoMove` hat nur einen Kandidaten).
  const hasSlotMoves = monsterQ.data.moves.some((m) => !m.isIntrinsic);

  // Offenes Angebot aus `monsters.pending_move_choice` (via listMonsterMoves).
  const pendingOffer = moveSlotsQ.data?.pending ?? null;

  return (
    // 720 px Lesebreite wie im Design-Kit (BattleScreen.jsx). Ohne die Grenze
    // zieht ein Breitbild-Monitor die HP-Balken auf anderthalb Meter und lässt
    // das Art-Fenster als Briefmarke daneben stehen.
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-3 p-3 pb-24">
      <ExpeditionReadyBanner />
      <header className="flex items-start justify-between gap-2">
        <div>
          <MicroLabel>
            <InfoTip text="Zone = größerer Weltabschnitt mit eigenem Leittyp und eskalierendem Level.">
              Zone {zone.id} · {zone.nameDe}
            </InfoTip>
          </MicroLabel>
          <div className="font-display text-lg tabular-nums">
            <InfoTip
              text={`Stage ${activeStageIndex} von ${zone.stageCount}. Ein Sieg auf deiner Frontier-Stage schaltet die nächste frei; frühere Stages spielst du zum Trainieren (halbe Belohnung).`}
            >
              Stage {activeStageIndex}/{zone.stageCount}
            </InfoTip>
            {stageDef.isCheckpoint && (
              <InfoTip text="Checkpoint: bei Niederlage in späteren Stages startest du von hier neu.">
                <Badge variant="outline" className="ml-2">
                  Checkpoint
                </Badge>
              </InfoTip>
            )}
            {stageDef.isBossStage && (
              <InfoTip text="Boss-Stage: Sieg schaltet die nächste Zone frei.">
                {/* Gold ist im Design die Bossfarbe (TierBadge, Panel tone
                    „boss"). Das alte Fuchsia stammt aus der Neon-Sprache und
                    kollidierte mit Pink = Elite. */}
                <Badge variant="boss" className="ml-2">
                  Boss
                </Badge>
              </InfoTip>
            )}
            {!isFrontierSelection && (
              <InfoTip text="Training: du kämpfst unterhalb deiner Frontier. XP/Gold zählen 50 %, dein Fortschritt bleibt unverändert.">
                <Badge variant="secondary" className="ml-2">
                  Training
                </Badge>
              </InfoTip>
            )}
          </div>
        </div>
      </header>

      {/* Offenes Lern-Angebot, das der Spieler weggeklickt hat: Es lebt in
          `monsters.pending_move_choice` weiter und blockiert dort JEDES
          weitere Angebot, war aber nach dem Schließen des Kampf-Popups
          nirgends mehr sichtbar. Vor dem Kampf steht der Hinweis hier, im
          Kampf als Badge über der Slot-Leiste. */}
      {!inFight && pendingOffer && (
        <button
          type="button"
          onClick={() => setOfferDialogOpen(true)}
          className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border-2 border-accent bg-card px-3 py-2 text-left text-xs shadow-hard-sm transition active:translate-y-[4px] active:shadow-none"
        >
          <span className="text-muted-foreground">
            <span className="font-extrabold text-foreground">Offene Attacken-Entscheidung:</span>{" "}
            {pendingOffer.move.name} wartet auf deine Antwort. Solange sie offen ist, kommt kein
            weiteres Angebot — bei vollen Slots lernt dein Xenomon dann nichts mehr dazu.
          </span>
          <span className="shrink-0 font-display text-[10px] uppercase tracking-[0.1em]">
            Jetzt entscheiden →
          </span>
        </button>
      )}

      {!inFight ? (
        <>
          <StageSelector
            zones={zonesQ.data ?? []}
            progress={progress}
            currentZoneStages={zoneQ.data.stages}
            selectedZone={activeZoneId}
            selectedStageIndex={activeStageIndex}
            onSelectZone={(z) => {
              setSelectedZone(z);
              // In der Frontier-Zone: Frontier-Stage vorwählen. In einer
              // bereits abgeschlossenen Zone: Stage 1.
              setSelectedStageIndex(z === progress.currentZone ? progress.currentStageIndex : 1);
            }}
            onSelectStage={setSelectedStageIndex}
          />
          <PreFight
            onStart={() => setInFight(true)}
            stageEnemyCount={stageScript.enemies.length}
            isFrontier={isFrontierSelection}
            hasSlotMoves={hasSlotMoves}
            frontierZone={progress.currentZone}
            frontierStageIndex={progress.currentStageIndex}
            onJumpToFrontier={jumpToFrontier}
            autoCast={autoCast}
            setAutoCast={setAutoCast}
            autoAdvance={autoAdvance}
            setAutoAdvance={setAutoAdvance}
          />
        </>
      ) : (
        <LiveArena
          key={`${activeZoneId}-${activeStageIndex}-${seed}`}
          player={monsterQ.data}
          stage={stageScript}
          seed={seed}
          zoneId={activeZoneId}
          stageIndex={activeStageIndex}
          isFrontier={isFrontierSelection}
          onJumpToFrontier={jumpToFrontier}
          autoCast={autoCast}
          setAutoCast={setAutoCast}
          autoAdvance={autoAdvance}
          setAutoAdvance={setAutoAdvance}
          onAdvance={advance}
          onExit={() => setInFight(false)}
          bossNameBySlug={
            zoneQ.data?.boss
              ? { [zoneQ.data.boss.slug]: zoneQ.data.boss.name_de }
              : {}
          }
          zoneNameDe={zoneQ.data?.zone.nameDe ?? null}
          slotByMoveId={slotByMoveId}
          pendingOfferName={pendingOffer?.move.name ?? null}
          onOpenOffer={() => setOfferDialogOpen(true)}
        />
      )}

      {/* Derselbe Dialog wie nach einem Level-Up, hier ohne frisch gelernte
          Attacke: `MoveLearnedModal` lädt das Angebot selbst aus
          `listMonsterMoves` und löst es über `resolveMoveChoice` auf. */}
      {offerDialogOpen && (
        <MoveLearnedModal
          open
          learned={[]}
          offerOpen
          onClose={() => setOfferDialogOpen(false)}
        />
      )}
    </div>
  );
}

function StageSelector({
  zones,
  progress,
  currentZoneStages,
  selectedZone,
  selectedStageIndex,
  onSelectZone,
  onSelectStage,
}: {
  zones: ZoneListEntry[];
  progress: {
    currentZone: number;
    currentStageIndex: number;
    highestZoneUnlocked: number;
  };
  currentZoneStages: { index: number; isBossStage: boolean; isCheckpoint: boolean }[];
  selectedZone: number;
  selectedStageIndex: number;
  onSelectZone: (zoneId: number) => void;
  onSelectStage: (stageIndex: number) => void;
}) {
  const unlockedZones = zones.filter((z) => z.id <= progress.highestZoneUnlocked);
  const selectedZoneEntry = zones.find((z) => z.id === selectedZone);
  const isFrontierZone = selectedZone === progress.currentZone;
  const maxStage = isFrontierZone
    ? progress.currentStageIndex
    : (selectedZoneEntry?.stageCount ?? currentZoneStages.length);
  const stageMeta = new Map(currentZoneStages.map((s) => [s.index, s]));

  return (
    // Das Tutorial zielt per `[data-tutorial-target="zone-up"]` auf diesen
    // Kasten (tutorial-steps.ts) und misst nur sein Rechteck. Das Attribut
    // sitzt deshalb auf einem Wrapper: `Panel` reicht keine Fremd-Attribute
    // durch, und der Wrapper hat als Block dieselben Maße.
    <div data-tutorial-target="zone-up">
      <Panel>
        <MicroLabel className="mb-2">
          <InfoTip text="Wähle Zone und Stage. Auf deiner Frontier-Stage zählt der volle Fortschritt; frühere Stages sind Training mit halber Belohnung.">
            <span>Stage-Auswahl ⓘ</span>
          </InfoTip>
        </MicroLabel>

        {/* Zonen-Wahl über die gemeinsamen Filter-Pillen (ScreenKit) — dieselbe
            Auswahl-Grammatik wie in Inventar, Basar und Brutkammer, inklusive
            der 44-px-Touch-Höhe und `aria-pressed`. */}
        <FilterPills<number>
          items={unlockedZones.map((z) => [z.id, `Z${z.id} · ${z.nameDe}`] as const)}
          value={selectedZone}
          onChange={onSelectZone}
        />

        {zones.length > 0 && progress.highestZoneUnlocked >= zones.length && (
          <div className="mt-2 text-xs text-muted-foreground">
            Derzeit sind {zones.length} Zonen verfügbar. Weitere Zonen werden in zukünftigen
            Updates hinzugefügt!
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {Array.from({ length: maxStage }, (_, i) => i + 1).map((idx) => {
            const meta = stageMeta.get(idx);
            const active = idx === selectedStageIndex;
            const boss = meta?.isBossStage;
            const cp = meta?.isCheckpoint;
            // Outline trägt die Bedeutung wie bei `Panel`: Gold = Boss.
            // Die gewählte Stage ist gefüllt — normal im Art-Blau (`accent`),
            // der Boss in seinem Gold. Orange bleibt dem einen Aktionsknopf
            // dieser Ansicht („Stage starten") vorbehalten.
            const tone = boss
              ? active
                ? "border-boss bg-boss text-ink"
                : "border-boss bg-secondary text-boss"
              : active
                ? "border-ink bg-accent text-accent-foreground"
                : "border-ink bg-secondary text-muted-foreground";
            return (
              <button
                key={idx}
                type="button"
                onClick={() => onSelectStage(idx)}
                aria-pressed={active}
                className={`flex h-11 min-w-11 flex-col items-center justify-center gap-0.5 rounded-md border-2 px-2 font-display text-[11px] leading-none tabular-nums shadow-hard-sm transition active:translate-y-[4px] active:shadow-none sm:h-10 sm:min-w-10 ${tone}`}
                title={
                  boss ? `Stage ${idx} · Boss` : cp ? `Stage ${idx} · Checkpoint` : `Stage ${idx}`
                }
              >
                <span>{idx}</span>
                {/* Icons statt Deko-Emoji, in derselben Bildsprache wie das
                    TierBadge (Krone = Boss). Beide erben die Textfarbe der
                    Kachel und bleiben damit in jedem Zustand lesbar. */}
                {boss ? (
                  <Crown className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                ) : cp ? (
                  <Flag className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}


function HowItWorks() {
  const [open, setOpen] = useState(false);
  return (
    <Panel contentClassName="p-0 text-xs">
      <button
        type="button"
        className="flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-left sm:min-h-9"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-display text-[12px] uppercase tracking-[0.08em]">
          So funktioniert Battle v2
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={2.5}
          aria-hidden
        />
      </button>
      {open && (
        <div className="border-t-2 border-ink px-3 py-2 text-muted-foreground">
          <p>
            Jede Attacke ist ab dem Startsignal sofort verfügbar und läuft dann immer denselben
            Zyklus: <span className="text-foreground">ausholen → Treffer → Abklingzeit</span>. Der
            Schaden fällt also nicht beim Auslösen, sondern am Ende des Ausholens — erst danach
            beginnt die Abklingzeit. Wie lange ausgeholt wird, steht auf jeder Slot-Kachel; die
            Regel läuft umgekehrt zur Wucht: leichte Attacken holen am längsten aus, wuchtige am
            kürzesten, die Ultimate am längsten von allen.
          </p>
          <p className="mt-1">
            Jede Attacke holt für sich aus — mehrere können das gleichzeitig tun, jede mit ihrem
            eigenen Zyklus. Nur dieselbe Attacke kann nicht doppelt ausholen: Ihre Kachel bleibt
            gesperrt, bis ihr Schlag gelandet ist, alle anderen bleiben antippbar. Selbst
            abbrechen kannst du ein Ausholen nicht, und ein gewöhnlicher Treffer stört es auch
            nicht; stirbt das Ziel währenddessen, trifft der nachgerückte Gegner. Manche Attacken
            reißen ein Ziel aber aus dem Ausholen: Alles, was dort unterwegs war, verpufft, und die
            Abklingzeiten laufen trotzdem an — das gilt für dich wie für den Gegner.
          </p>
          <p className="mt-1">
            <span className="text-foreground">Typen zählen an der Attacke.</span> Ob ein Schlag im
            Vorteil landet, entscheidet der Typ der <em>Attacke</em>, nicht der deines Xenomons —
            der Punkt auf jeder Slot-Kachel zeigt ihn, ein Pfeil daneben heißt Vor- oder Nachteil.
            Ein grauer Punkt ohne Pfeil ist eine neutrale Attacke: immer gleich stark. Hilfs-Attacken
            ohne Schaden lösen nie einen Vorteil aus, und in manchen Zonen sind Typen ganz
            ausgesetzt — dann trägt keine Kachel einen Pfeil. Nur der Basis-Angriff schlägt mit dem
            Typ deines Xenomons zu.
          </p>
          <p className="mt-1">
            <span className="text-foreground">Neue Stage vs. Training:</span> Auf deiner
            Frontier-Stage — der höchsten, die du noch nicht geschafft hast — ist der Auto-Cast aus.
            Du tippst dort jede Attacke selbst; nur der eingebaute Basis-Angriff (Power ×
            {de(INTRINSIC_BASIC_POWER, 2)}, ohne Ausholen, alle{" "}
            {de(INTRINSIC_BASIC_COOLDOWN_MS / 1000, 1)}s) feuert weiter von allein. Solange dein
            Xenomon noch keine Attacke gelernt hat, gibt es dort nichts zu tippen — der Kampf läuft
            dann komplett von allein; die erste Attacke kommt mit dem Aufstieg auf Level 2. Auf
            jeder bereits geschafften Stage (Training) bleibt der Auto-Cast bedienbar: Dein Xenomon
            holt dann selbst aus, sobald ein Move bereit ist — pro Tick startet er höchstens einen,
            danach laufen mehrere Casts nebeneinander. Selbst antippen kannst du dort trotzdem jeden
            Move, dessen Abklingzeit vorbei ist und der nicht schon selbst ausholt.
          </p>
          <p className="mt-1">
            Sieg → nächste Stage. Niederlage → zurück zum letzten Checkpoint. Der Aktiv-Bonus
            {ACTIVE_BONUS_SUMMARY} zählt nur selbst getippte Casts auf deine Slot-Attacken — der
            Basis-Angriff feuert immer von allein und zählt deshalb nie. Auf einer neuen Stage geht
            jeder Cast auf dein Konto; im Training zählen XP und Gold 50 %, und der Auto-Cast kommt
            dir bei vielen Moves zuvor. Der Deckel ist bewusst schwer: Zwischen zwei gewerteten
            Casts derselben Attacke muss ein voller Zyklus liegen, mit wenigen Attacken ist er in
            einem normalen Kampf nicht zu schaffen.
          </p>
        </div>
      )}
    </Panel>
  );
}

function PreFight({
  onStart,
  stageEnemyCount,
  isFrontier,
  hasSlotMoves,
  frontierZone,
  frontierStageIndex,
  onJumpToFrontier,
  autoCast,
  setAutoCast,
  autoAdvance,
  setAutoAdvance,
}: {
  onStart: () => void;
  stageEnemyCount: number;
  isFrontier: boolean;
  /**
   * Trägt der Kampf-Snapshot mindestens eine Slot-Attacke? Nur dann gibt es
   * etwas zu tippen — ohne sie ist die Frontier-Regel ein No-Op und weder die
   * Ansage noch der Auto-Cast-Schalter dürfen etwas anderes behaupten.
   */
  hasSlotMoves: boolean;
  frontierZone: number;
  frontierStageIndex: number;
  onJumpToFrontier: () => void;
  autoCast: boolean;
  setAutoCast: (v: boolean) => void;
  autoAdvance: boolean;
  setAutoAdvance: (v: boolean) => void;
}) {
  // Derselbe Testschalter wie in der Arena. Er wird hier eigenstaendig
  // gelesen statt durchgereicht, weil er aus dem localStorage kommt und beide
  // Stellen ohnehin denselben Wert sehen — im Production-Build konstant false.
  const devFrontierAutoCast = useFrontierAutoCast();
  const frontierSperre = isFrontier && !devFrontierAutoCast;
  // Dieselbe Pruefung wie in der Arena. React Query fasst beide Aufrufe unter
  // demselben Schluessel zusammen, es kostet also keine zweite Runde.
  const veralteteVersion = useVeralteteKernelVersion();
  return (
    // Kein farbiger Außenrand mehr für „Training vs. Frontier": Welcher Modus
    // gilt, sagt der Kasten darin in Worten — die Outline trägt im Design nur
    // Tiefe und Bedeutung, nicht die Wiederholung einer Ansage.
    <Panel contentClassName="p-4">
      {!isFrontier && (
        <Panel tone="primary" className="mb-3">
          <div className="flex items-center gap-2 font-display text-[12px] uppercase tracking-[0.08em]">
            <Info className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
            Trainingsmodus
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Du bist nicht auf deiner Frontier-Stage (aktuell{" "}
            <span className="font-semibold tabular-nums text-foreground">
              Zone {frontierZone}, Stage {frontierStageIndex}
            </span>
            ). XP und Gold zählen 50 %, dein Fortschritt bleibt unverändert.
          </p>
          <Button size="sm" variant="secondary" className="mt-2" onClick={onJumpToFrontier}>
            Zur Frontier springen →
          </Button>
        </Panel>
      )}
      <p className="text-sm text-muted-foreground">
        {stageEnemyCount} Gegner in dieser Stage — sie treten nacheinander an. Kampf startet mit
        vollen HP.
      </p>
      {/* Frontier-Regel, bevor der Kampf läuft: Der Spieler soll wissen, dass
          er hier selbst tippen muss — nicht erst, wenn nichts passiert. Ohne
          Slot-Attacke gibt es aber nichts zu tippen (siehe FRONTIER_MANUAL_*),
          dann bleibt der Kasten weg. */}
      {isFrontier && hasSlotMoves && (
        <Panel tone="primary" className="mt-3">
          <div className="flex items-center gap-2 font-display text-[12px] uppercase tracking-[0.08em]">
            <Hand className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
            {FRONTIER_MANUAL_HEADLINE}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{FRONTIER_MANUAL_BODY}</p>
        </Panel>
      )}
      <div className="mt-3 flex flex-col gap-2 text-xs">
        {/* Erklärung als eigenes ⓘ neben dem Label: der InfoTip-Trigger
            schluckt seinen Tap (sonst kippt er die Checkbox mit um), das
            Label-Wort selbst muss aber schaltbar bleiben. */}
        {/* Ohne Slot-Attacke ergibt der Schalter in BEIDEN Stellungen exakt
            denselben Kampf (der Auto-Cast hat nur den Grundangriff als
            Kandidaten) — dann wird er gar nicht erst gezeigt, statt eine
            Wahl vorzugaukeln. Die gespeicherte Vorliebe bleibt unberührt. */}
        {hasSlotMoves && (
        <div className="flex items-center gap-2">
          {/* Auf der Frontier ist der Schalter gesperrt und zeigt „aus" — der
              SERVER rechnet den Kampf dort mit autoCastEnabled=false nach
              (settle.functions.ts). Ein bedienbarer Schalter würde entweder
              lügen oder die Hash-Parität brechen. Die gespeicherte Vorliebe
              (localStorage) bleibt unangetastet und gilt weiter im Training. */}
          <label
            className={`flex items-center gap-2 ${frontierSperre ? "opacity-60" : ""}`}
            title={frontierSperre ? FRONTIER_MANUAL_TIP : undefined}
          >
            <input
              type="checkbox"
              checked={frontierSperre ? false : autoCast}
              disabled={frontierSperre}
              // Zweite Sicherung neben `disabled`, symmetrisch zur Kampf-
              // Checkbox: Dieser Wert geht über `frozenAutoCastRef` in den
              // Kampf und damit in den Hash. Ein Toggle, der das `disabled`
              // umgeht (Autofill, Devtools, künftiges Refactoring), würde die
              // Server-Regel `isFrontier → false` brechen.
              onChange={(e) => {
                if (frontierSperre) return;
                setAutoCast(e.target.checked);
              }}
            />
            <span>
              Auto-Cast
              {frontierSperre ? " · auf neuen Stages aus" : ""}
              {isFrontier && !frontierSperre ? " · Testschalter" : ""}
            </span>
          </label>
          <InfoTip text={frontierSperre ? FRONTIER_MANUAL_TIP : AUTO_CAST_TIP}>
            <span className={INFO_DOT_CLASS} aria-label="Auto-Cast erklärt">
              ⓘ
            </span>
          </InfoTip>
        </div>
        )}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
            />
            <span>Auto-Fortschritt</span>
          </label>
          <InfoTip text="An: Nach einem Sieg startet nach 2 Sekunden automatisch die nächste Stage — bis zur ersten Niederlage oder bis du stoppst. Im Training läuft es innerhalb der bereits freigeschalteten Zonen weiter, ohne den Frontier-Fortschritt zu verändern.">
            <span className={INFO_DOT_CLASS} aria-label="Auto-Fortschritt erklärt">
              ⓘ
            </span>
          </InfoTip>
        </div>
      </div>

      <PreFightConsumables />

      {/* Veraltetes Bündel: NICHT starten lassen. Ein Hinweis allein würde
          überlesen, und der Preis ist ein ganzer ausgefochtener Kampf. */}
      {veralteteVersion ? (
        <div className="mt-3 rounded-md border-2 border-ink bg-amber-500/15 p-3">
          <div className="font-display text-[12px] uppercase tracking-[0.08em]">
            Neue Version verfügbar
          </div>
          <p className="mt-1 text-[11px] leading-relaxed">
            Dieser Tab läuft noch mit dem Stand von vor der letzten
            Veröffentlichung und würde den Kampf anders ausrechnen als der Server —
            der Kampf ginge dabei verloren. Einmal neu laden, dann passt es.
          </p>
          <Button className="mt-2 w-full" onClick={seiteNeuLaden}>
            Neu laden
          </Button>
        </div>
      ) : (
        /* Der EINE orange Knopf dieser Ansicht. Alles andere hier — Zonen- und
           Stage-Wahl, Verbrauchsgegenstände, „Zur Frontier springen" — bleibt
           bewusst sekundär. */
        <Button className="mt-3 w-full" onClick={onStart}>
          Stage starten
        </Button>
      )}
    </Panel>
  );
}

const CONSUMABLE_KINDS = new Set(["consumable"]);

type ConsumableItem = {
  id: string;
  name: string;
  kind: string;
  qty: number;
  rarity?: string | null;
  stats?: Record<string, number> | null;
  description?: string | null;
};

const RARITIES: Rarity[] = ["common", "rare", "epic", "legendary"];

/** `items.rarity` ist in der DB ein freier Text — hier auf die vier Stufen geklemmt. */
function asRarity(raw: string | null | undefined): Rarity {
  return RARITIES.find((r) => r === raw) ?? "common";
}

function summarizeStats(stats: Record<string, number> | null | undefined): string {
  if (!stats) return "";
  const parts: string[] = [];
  const push = (label: string, key: string) => {
    const v = stats[key];
    if (typeof v === "number" && v !== 0) parts.push(`${v > 0 ? "+" : ""}${v} ${label}`);
  };
  push("HP", "hp");
  return parts.join(" · ");
}

function PreFightConsumables() {
  const qc = useQueryClient();
  const fetchItems = useServerFn(listItems);
  const fetchMon = useServerFn(getMyMonster);
  const consumeItemFn = useServerFn(useConsumable);

  const itemsQ = useQuery({
    queryKey: ["items"],
    queryFn: () => fetchItems(),
    staleTime: 10_000,
  });

  const consumeMut = useMutation({
    mutationFn: (itemId: string) => consumeItemFn({ data: { itemId } }),
    onSuccess: () => {
      toast.success("Item verwendet.");
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["my-monster"] });
      // ["battle-monster"] gehoert dem Expeditions-Bildschirm, nicht dem
      // Kampf — der liest ["battle-v2","monster"]. Beide anstossen.
      qc.invalidateQueries({ queryKey: ["battle-monster"] });
      qc.invalidateQueries({ queryKey: ["battle-v2", "monster"] });
      qc.invalidateQueries({ queryKey: ["combat-state"] });
    },
    onError: (e: Error) => toast.error(e.message ?? "Item konnte nicht benutzt werden."),
  });

  const consumables = ((itemsQ.data?.items ?? []) as ConsumableItem[]).filter((it) =>
    CONSUMABLE_KINDS.has(it.kind),
  );

  return (
    <Panel className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <MicroLabel>Verbrauchsgegenstände</MicroLabel>
        <InfoTip text="Wende Heil-, Futter- oder Medizin-Items vor dem Kampf an. Während des Kampfes sind Items deaktiviert.">
          <span className="text-[10px] text-muted-foreground">Vor dem Kampf ⓘ</span>
        </InfoTip>
      </div>
      {itemsQ.isLoading ? (
        <p className="text-xs text-muted-foreground">Lade Inventar…</p>
      ) : consumables.length === 0 ? (
        <p className="text-xs text-muted-foreground">Keine Verbrauchsgegenstände verfügbar.</p>
      ) : (
        <ul className="grid max-h-40 grid-cols-1 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
          {consumables.map((it) => {
            const summary = summarizeStats(it.stats);
            const busy =
              consumeMut.isPending && (consumeMut.variables as string | undefined) === it.id;
            return (
              <li key={it.id}>
                <button
                  type="button"
                  disabled={consumeMut.isPending}
                  onClick={() => {
                    if (typeof fetchMon === "function") void fetchMon();
                    consumeMut.mutate(it.id);
                  }}
                  className="flex min-h-11 w-full items-center gap-2 rounded-md border-2 border-ink bg-secondary px-2 py-1.5 text-left text-[11px] shadow-hard-sm transition active:translate-y-[4px] active:shadow-none disabled:cursor-not-allowed disabled:opacity-45"
                  title={summary || it.description || it.name}
                >
                  {/* Dieselbe Item-Kachel wie im Inventar (ScreenKit): Fläche
                      in der Raritätsfarbe, Ink-Outline, lucide-Symbol. */}
                  <ItemIcon
                    icon={<FlaskConical className="h-4 w-4" strokeWidth={2.5} />}
                    rarity={asRarity(it.rarity)}
                    size={28}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-extrabold text-foreground">
                      {it.name}{" "}
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        ×{it.qty}
                      </span>
                    </span>
                    {summary && (
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {summary}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 font-display text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    {busy ? "…" : "Benutzen"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}


/** Turn a combat-core enemy id (`slug#idx`) into a human-readable label. */
function enemyLabel(id: string): string {
  const slug = id.split("#")[0];
  const entry = catalogEntryBySlug(slug);
  if (entry) return entry.name.de;
  const parts = slug.split("-");
  if (parts.length >= 2) {
    const type = parts[0];
    const tier = parts[1];
    const tierLabel = tier === "elite" ? "Elite" : tier === "boss" ? "Boss" : "";
    const typeLabel = type ? type.charAt(0).toUpperCase() + type.slice(1) : slug;
    return tierLabel ? `${typeLabel}-${tierLabel}-Gegner` : `${typeLabel}-Gegner`;
  }
  return slug;
}

/**
 * Deutsche Wörter für die Statuszustände des Kernels.
 *
 * `StatusFlag` (`burn`, `blind`, `corrupt`, `lag`, `disrupt` —
 * combat-core/types.ts) ist ein interner Bezeichner. Er stand bis hierher roh
 * in der deutschen Oberfläche: im Move-Tooltip der Slot-Leiste („Effekt: burn")
 * und im Kampf-Log („Du bist jetzt burn.").
 *
 * `name` steht dort, wo der Effekt benannt wird (Tooltip), `praedikat` dort, wo
 * er in einem Satz steht (Log).
 *
 * ⚠️ `disrupt` fehlt bewusst und wird dadurch NIRGENDS angezeigt: Der Status
 * wird zwar gesetzt, aber kein Modul des Kernels liest ihn. Ihn zu benennen
 * hieße, eine Wirkung zu behaupten, die es nicht gibt.
 * (Der frühere Verweis auf C7-A ist überholt: Seit dem 07.09.2026 bricht eine
 * Attacke mit `interrupts` sehr wohl laufende Cast-Phasen ab — nur eben nicht
 * über diesen Status.)
 */
const STATUS_LABEL: Partial<Record<StatusFlag, { name: string; praedikat: string }>> = {
  // ⚠️ NEUTRAL. Der Kernel kennt einen Zustand, der Katalog zwei
  // Handschriften — Plasma brennt, Void zersetzt (`lib/game/status-worte.ts`).
  // Hier steht die Mechanik, weil weder `StatusInstance` noch die Ereignisse
  // `status_apply`/`dot_tick` eine Attacken-Kennung tragen: Wer den Typ nicht
  // kennt, darf ihn nicht behaupten. Der Move-Tooltip unten kennt ihn und
  // benennt ihn richtig.
  burn: { name: DOT_NEUTRAL, praedikat: "von Schaden über Zeit betroffen" },
  blind: { name: "Blendung", praedikat: "geblendet" },
  corrupt: { name: "Korruption", praedikat: "korrumpiert" },
  lag: { name: "Verzögerung", praedikat: "verlangsamt" },
};

/**
 * Nachschlagen mit einem rohen String (das Log-Ereignis `status_apply` trägt
 * `status` als `string`). Ohne Treffer gibt es kein deutsches Wort — dann wird
 * nichts angezeigt, statt den Bezeichner durchzureichen.
 */
function statusWord(kind: string): { name: string; praedikat: string } | undefined {
  for (const [flag, word] of Object.entries(STATUS_LABEL)) {
    if (flag === kind) return word;
  }
  return undefined;
}

type CombatEventLite = { t: number; kind: string } & Record<string, unknown>;

/**
 * Renders a raw combat-core event into a German, human-readable sentence
 * for the in-fight log. Returns `null` for kinds we intentionally hide
 * (chatty low-signal ticks like `dot_tick`, `combo_reset`, `spawn`).
 */
function formatCombatEvent(
  ev: CombatEventLite,
  playerId: string,
  playerLabel: string,
  labelForEnemy: (id: string) => string,
  moveNameById: Map<string, string>,
): { text: string; className: string } | null {
  const nameFor = (id: string, asTarget: boolean) => {
    if (id === playerId) return asTarget ? "dich" : playerLabel;
    return labelForEnemy(id);
  };
  /**
   * Deutsche Verb-Kongruenz. Der Spieler heißt im Log „Du", also braucht er die
   * 2. Person: „Du triffst", nicht „Du trifft". Ohne das lasen sich fünf der
   * neun Log-Zeilen falsch, sobald der Spieler das Subjekt war — bei „setzt ein"
   * fiel es nur deshalb nicht auf, weil die Form dort für beide gleich ist.
   */
  const verb = (subjekt: string | null, du: string, andere: string) =>
    subjekt === playerId ? du : andere;
  const moveName = (id: string) => moveNameById.get(id) ?? (id === "basic" ? "Angriff" : id);

  const src = typeof ev.source === "string" ? ev.source : null;
  const tgt = typeof ev.target === "string" ? ev.target : null;
  const dmg = typeof ev.damage === "number" ? ev.damage : null;
  const enemyIdRaw = typeof ev.enemyId === "string" ? ev.enemyId : null;
  const moveId = typeof ev.moveId === "string" ? ev.moveId : null;

  switch (ev.kind) {
    case "hit": {
      if (!src || !tgt || dmg === null) return null;
      const from = nameFor(src, false);
      const to = nameFor(tgt, true);
      const cls =
        src === playerId
          ? "text-emerald-300"
          : tgt === playerId
            ? "text-rose-300"
            : "text-muted-foreground";
      return {
        text: `${from} ${verb(src, "triffst", "trifft")} ${to} für ${dmg} Schaden.`,
        className: cls,
      };
    }
    case "crit": {
      if (!src || !tgt || dmg === null) return null;
      const from = nameFor(src, false);
      const to = nameFor(tgt, true);
      return {
        text: `${from} ${verb(src, "triffst", "trifft")} ${to} kritisch für ${dmg} Schaden! 💥`,
        className: "text-yellow-300 font-semibold",
      };
    }
    case "special_cast": {
      if (!src || !moveId) return null;
      return {
        text: `${nameFor(src, false)} setzt „${moveName(moveId)}" ein.`,
        className: src === playerId ? "text-emerald-200" : "text-rose-200 font-semibold",
      };
    }
    /**
     * Unterbrechung. Der Kernel schreibt dieses Ereignis seit dem 07.09.2026
     * (18 der 180 Attacken tragen `interrupt`), aber hier gab es keinen Zweig
     * dafür — die Zeile fiel in `default` und wurde verschluckt. Es passierte
     * also etwas Wirksames, und der Spieler erfuhr es nie (Stefan, 09.09.2026).
     *
     * `moveIds` sind die Attacken des Ziels, deren Ausholen abgebrochen wurde.
     * Bei einer nennen wir sie beim Namen, bei mehreren nur die Zahl — sonst
     * wird die Zeile länger als das, was sie erklärt.
     */
    case "cast_interrupted": {
      if (!src || !tgt) return null;
      const ids = Array.isArray(ev.moveIds) ? (ev.moveIds as string[]) : [];
      const was =
        ids.length === 1
          ? `„${moveName(ids[0])}"`
          : ids.length > 1
            ? `${ids.length} Attacken`
            : "das Ausholen";
      return {
        text: `${nameFor(src, false)} ${verb(src, "unterbrichst", "unterbricht")} ${nameFor(tgt, true)}: ${was} abgebrochen.`,
        className: src === playerId ? "text-emerald-200 font-semibold" : "text-rose-200 font-semibold",
      };
    }
    case "mon_defeated": {
      const monId = typeof ev.monsterId === "string" ? ev.monsterId : null;
      const who = monId ? nameFor(monId, false) : "Ein Kämpfer";
      return {
        text: `${who} ${verb(monId, "wurdest", "wurde")} besiegt.`,
        className: "text-fuchsia-300 font-semibold",
      };
    }
    case "enemy_defeated": {
      if (!enemyIdRaw) return null;
      return {
        text: `${labelForEnemy(enemyIdRaw)} wurde besiegt.`,
        className: "text-emerald-300 font-semibold",
      };
    }
    case "heal": {
      const amt = typeof ev.amount === "number" ? ev.amount : 0;
      if (!tgt || amt <= 0) return null;
      return {
        text: `${nameFor(tgt, false)} ${verb(tgt, "regenerierst", "regeneriert")} ${amt} HP.`,
        className: "text-emerald-200",
      };
    }
    case "mechanic_trigger": {
      const mech = typeof ev.mechanic === "string" ? ev.mechanic : "";
      const label =
        mech === "enrage"
          ? "gerät in Raserei"
          : mech === "phase_shift"
            ? "wechselt die Phase"
            : mech;
      if (!enemyIdRaw) return null;
      return {
        text: `${labelForEnemy(enemyIdRaw)} ${label}.`,
        className: "text-orange-300 font-semibold",
      };
    }
    case "status_apply": {
      if (!tgt) return null;
      const word = statusWord(typeof ev.status === "string" ? ev.status : "");
      // Ohne deutsches Wort wird nichts gemeldet. Betroffen ist genau
      // `disrupt` — den Status liest kein Modul des Kernels, eine Zeile
      // darüber würde dem Spieler eine Wirkung ankündigen, die nie eintritt.
      if (!word) return null;
      return {
        text: `${nameFor(tgt, false)} ${verb(tgt, "bist", "ist")} jetzt ${word.praedikat}.`,
        className: "text-violet-200",
      };
    }
    case "autocast_toggled": {
      return {
        text: ev.enabled === true ? "Auto-Cast an." : "Auto-Cast aus.",
        className: "text-muted-foreground",
      };
    }
    // Intentionally hidden: dot_tick, combo_reset — zu chatty für den Live-Log.
    default:
      return null;
  }
}


/**
 * Farbpunkt plus Pfeil — dieselbe Sprache wie auf der Slot-Kachel.
 *
 * Ersetzt die frühere Zeile „Typ A vs Typ B ×1,15" (bis 09.09.2026). Die
 * beantwortete die Frage am KÄMPFER; seit dem 07.09.2026 hängt der Vorteil
 * aber an der ATTACKE — und ein Kämpfer trägt Attacken verschiedener Typen.
 *
 * Grau heißt „ohne Ring-Typ" (neutral): immer gleich stark. Ein Pfeil
 * erscheint NUR, wenn der Kampf wirklich einen anderen Faktor rechnet — bei
 * Hilfs-Attacken (Power 0) und in Zonen ohne Typenkreis also nie.
 */
function TypPunkt({ vorteil }: { vorteil: TypVorteil }) {
  return (
    <span
      title={typVorteilSatz(vorteil)}
      className="mr-1 inline-flex items-center gap-0.5 align-middle"
    >
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full border border-ink"
        style={{
          background: vorteil.typ
            ? `var(--type-${vorteil.typ})`
            : "var(--muted-foreground)",
        }}
      />
      {vorteil.pfeil && (
        <span
          className={
            vorteil.pfeil === "hoch"
              ? "text-[9px] leading-none text-emerald-300"
              : "text-[9px] leading-none text-rose-300"
          }
        >
          {vorteil.pfeil === "hoch" ? "▲" : "▼"}
        </span>
      )}
    </span>
  );
}

function StatCell({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between rounded border border-border/40 bg-background/40 px-2 py-1">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums text-foreground">{value}</span>
    </div>
  );
}

function MonsterInfoPanel({
  name,
  level,
  type,
  hp,
  hpMax,
  atk,
  def,
  moves,
  opponentType,
  ignoreTypeMatrix,
}: {
  name: string;
  level: number;
  type: CreatureType;
  hp: number;
  hpMax: number;
  atk: number;
  def: number;
  moves?: MoveSnapshot[];
  opponentType?: CreatureType;
  /** Setzt DIESE Stage den Typenkreis aus? Siehe TypeEffectivenessLine. */
  ignoreTypeMatrix: boolean;
}) {
  return (
    <div className="mt-3 rounded-md border-2 border-ink bg-secondary p-2 text-xs">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate font-display text-[12px] uppercase tracking-[0.08em] text-glow">
          {name} · Lv {level}
        </span>
        <TypeBadge type={type} />
      </div>
      {/* Drei Kacheln statt vier: SPD ist raus, weil Tempo im Zonenkampf in
          keiner Formel gelesen wird (Stefan, 09.09.2026). Die Spaltenzahl muss
          mit — sonst haengt am Handy eine Kachel allein in der zweiten Zeile,
          und der Kampf-Screen soll auf 390x844 ohne Scrollen passen. */}
      <div className="grid grid-cols-3 gap-1.5">
        <StatCell label="HP" value={`${hp}/${hpMax}`} />
        <StatCell label="ATK" value={atk} />
        <StatCell label="DEF" value={def} />
      </div>
      {/* Hier stand bis zum 09.09.2026 eine Zeile „Typ A vs Typ B ×1,15".
          Sie ist weg: Seit dem 07.09. entscheidet der Typ der ATTACKE, und ein
          Kämpfer trägt Attacken verschiedener Typen — eine Zeile über dem
          Ganzen kann das nicht mehr sagen. Die Aussage steht jetzt an jeder
          einzelnen Attacke darunter. */}
      {ignoreTypeMatrix && (
        <div className="mt-2 rounded-md border-2 border-ink bg-ink px-2 py-1.5 text-[10px] text-muted-foreground">
          Auf dieser Stage ist der Typenkreis ausgesetzt — jeder Treffer zählt ×1,00.
        </div>
      )}
      {moves && moves.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Moves</div>
          <ul className="space-y-1">
            {moves.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-2 rounded border border-border/40 bg-background/40 px-2 py-1"
              >
                <span className="min-w-0 flex-1 truncate">
                  {/* Der Typ stand hier schon — roh, englisch und ohne Aussage
                      („· void"). Jetzt trägt er Farbe und sagt, was er gegen
                      DIESEN Gegner bewirkt. Gerechnet wird in
                      `typVorteilFuerAnzeige`, damit Anzeige und Kampf nicht
                      auseinanderlaufen. */}
                  <TypPunkt
                    vorteil={typVorteilFuerAnzeige(m, type, opponentType, ignoreTypeMatrix)}
                  />
                  <span className="font-medium text-foreground">{m.name ?? m.id}</span>
                </span>
                {/* Kein „Cast Xs · CD Ys" mehr nebeneinander: die beiden Zahlen
                    ergeben addiert nur bei langen Cooldowns den Zyklus, bei
                    kurzen ist die Ausholzeit vom Cooldown schon abgezogen.
                    Angezeigt wird deshalb der tatsächliche Treffer-Takt. */}
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                  P {typeof m.power === "number" ? de(m.power, 2) : m.power} · {castCycleShort(m)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function InfoToggle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={label}
      className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 border-ink bg-secondary text-muted-foreground transition hover:text-foreground"
    >
      {open ? <ChevronDown className="h-3 w-3" /> : <Info className="h-3 w-3" />}
    </button>
  );
}



function LiveArena({
  player,
  stage,
  seed,
  zoneId,
  stageIndex,
  isFrontier,
  onJumpToFrontier,
  autoCast,
  setAutoCast,
  autoAdvance,
  setAutoAdvance,
  onAdvance,
  onExit,
  bossNameBySlug,
  zoneNameDe,
  slotByMoveId,
  pendingOfferName,
  onOpenOffer,
}: {
  player: Parameters<typeof useLiveCombat>[0]["player"];
  stage: StageScript;
  seed: number;
  zoneId: number;
  stageIndex: number;
  isFrontier: boolean;
  onJumpToFrontier: () => void;
  autoCast: boolean;
  setAutoCast: (v: boolean) => void;
  autoAdvance: boolean;
  setAutoAdvance: (v: boolean) => void;
  onAdvance: () => Promise<void>;
  onExit: () => void;
  bossNameBySlug: Record<string, string>;
  zoneNameDe: string | null;
  /** Echte monster_moves.slot-Nummer je Move-ID. Siehe `moveBySlot` unten. */
  slotByMoveId: Map<string, number>;
  /** Name der Attacke aus einem offenen `pending_move_choice`, sonst null. */
  pendingOfferName: string | null;
  onOpenOffer: () => void;
}) {
  // Freeze autoCast at mount alongside useLiveCombat's input freeze: this is
  // the fight's START value, sent as-is to settle. Mid-fight toggles travel as
  // inputLog entries (live.setAutoCast) — sending the current checkbox value
  // instead would make the server replay from a different start state → hash
  // mismatch though both sides agree on the outcome. Remount resets it.
  //
  // FRONTIER-REGEL (Stefan, 02.09.2026): Auf der Frontier-Stage ist der
  // Auto-Cast IMMER aus. Der Server rechnet genau so nach
  // (`autoCastEnabled = isFrontier ? false : data.autoCastEnabled` in
  // settle.functions.ts) und strippt zusätzlich die Auto-Cast-Toggles aus dem
  // inputLog. Jede Abweichung hier = anderer Kampfverlauf = Hash-Divergenz =
  // 409 auf dem Sieg. Deshalb wird derselbe Wert eingefroren, an
  // `useLiveCombat` gegeben, an den Settle geschickt und in den Log-Export
  // geschrieben — und `live.setAutoCast` ist im Frontier stillgelegt, damit
  // kein Toggle im inputLog landet.
  // Testschalter aus dem Dev-Panel (im Production-Build konstant false):
  // Er hebt die Frontier-Sperre auf — hier UND im Settle, sonst rechnen die
  // beiden Seiten verschiedene Kaempfe.
  const devFrontierAutoCast = useFrontierAutoCast();
  // ⚠️ Auch der Testschalter wird EINGEFROREN, nicht nur `autoCast`. Schaltet
  // jemand ihn mitten im Kampf um, schickte der Settle sonst den neuen Wert,
  // während der Kampf mit dem alten gespielt wurde — der Server rechnete einen
  // anderen Kampf nach, und der Sieg wäre mit "settle mismatch" weg. Genau
  // dieselbe Falle wie bei `autoCast`, nur eine Ebene höher.
  const frozenDevFrontierRef = useRef(devFrontierAutoCast);
  const frozenDevFrontier = frozenDevFrontierRef.current;
  const frontierSperre = isFrontier && !frozenDevFrontier;
  const frozenAutoCastRef = useRef(frontierSperre ? false : autoCast);
  const frozenAutoCast = frozenAutoCastRef.current;
  // Zeitraffer der Testumgebung: aendert NUR die Wiedergabe-Geschwindigkeit
  // (`intervalMs`), nie die Simulationsschrittweite `tickMs` — die steckt im
  // eingefrorenen CombatInput und ist hash-relevant. Der Kampf laeuft also
  // schneller ab, rechnet aber Zeichen fuer Zeichen dasselbe, und der Settle
  // stimmt. Im Production-Build gibt der Hook immer `undefined` zurueck.
  const devIntervalMs = useDevBattleIntervalMs();
  const live = useLiveCombat({
    player,
    stage,
    seed,
    autoStart: true,
    autoCastEnabled: frozenAutoCast,
    lockAutoCast: frontierSperre,
    intervalMs: devIntervalMs,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const writeLease = () => {
      window.localStorage.setItem(BATTLE_V2_ACTIVE_KEY, JSON.stringify({ owner, at: Date.now() }));
    };
    writeLease();
    const id = window.setInterval(writeLease, 5_000);
    return () => {
      window.clearInterval(id);
      try {
        const raw = window.localStorage.getItem(BATTLE_V2_ACTIVE_KEY);
        const parsed = raw ? (JSON.parse(raw) as { owner?: unknown }) : null;
        if (parsed?.owner === owner) window.localStorage.removeItem(BATTLE_V2_ACTIVE_KEY);
      } catch {
        window.localStorage.removeItem(BATTLE_V2_ACTIVE_KEY);
      }
    };
  }, []);

  const snap = live.snapshot;
  const qc = useQueryClient();
  const { trigger: triggerTutorial } = useTutorialCtx();
  const settle = useServerFn(settleStage);
  const [settleState, setSettleState] = useState<
    | { status: "idle" }
    | { status: "pending" }
    | {
        status: "done";
        outcome: "win" | "loss";
        xp: number;
        gold: number;
        itemsCreated: number;
        // Serverwerte des Auto-Salvage — was weggeräumt wurde, gehört genauso
        // ins Ergebnis wie das, was liegen blieb.
        autoSalvaged: number;
        autoSalvageGold: number;
        activeBonusPct: number;
        isFrontier: boolean;
        rewardMult: number;
      }
    | { status: "error"; message: string }
  >({ status: "idle" });

  // Lern-Popup nach dem Kampf: Was kann die neue Attacke — und liegt ein
  // Angebot an, weil alle Slots voll sind? Ein Modal für ALLE Level dieses
  // Settles (kein Popup-Stapel), deshalb eine Liste statt eines Eintrags.
  const [learnResult, setLearnResult] = useState<{
    learned: LearnedMoveInfo[];
    offerOpen: boolean;
  } | null>(null);

  // Player-name for log: fall back to short id if the monster row didn't
  // carry a nickname through the snapshot (we only have id here).
  const playerLabel = "Du";

  // Move-Namen für den Log (id → Name). Fällt auf die id zurück, wenn
  // der Snapshot keinen Namen mitgeliefert hat (z.B. Enemy-Auto-Move).
  const moveNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const mv of player.moves) map.set(mv.id, moveDisplayName(mv));
    // Enemy moves carry a display name via the pool loader (name column).
    // Without adding them here the log falls back to raw move UUIDs.
    for (const e of stage.enemies) {
      for (const mv of e.moves) if (mv.name) map.set(mv.id, mv.name);
    }
    return map;
  }, [player.moves, stage.enemies]);

  // Cooldowns für die Aktiv-Bonus-Zählung (Server-Regel, siehe
  // countScoredCasts). `live.inputLog` ist dieselbe Array-Referenz über den
  // ganzen Kampf und wird mutiert — deshalb bewusst kein useMemo darauf,
  // sondern Neuberechnung pro Render (der Tick rendert ohnehin 10×/s).
  const playerMoveCds = useMemo(
    () => new Map(player.moves.map((m) => [m.id, (m.castMs ?? 0) + m.cooldownMs])),
    [player.moves],
  );
  const scoredCasts = countScoredCasts(live.inputLog, playerMoveCds);


  // Item-IDs, die der Client VOR dem Settle kannte. Die Beute-Anzeige
  // erkennt daran, welche Zeilen wirklich aus DIESEM Kampf stammen — der
  // Server liefert nur eine Anzahl zurück. `null`, wenn nichts im Cache
  // stand; dann faellt LootReveal auf „die neuesten N" zurueck.
  const preSettleItemIds = useRef<string[] | null>(null);

  // Fire settle exactly once when the combat finishes.
  useEffect(() => {
    if (!live.result || settleState.status !== "idle") return;
    const cachedItems = qc.getQueryData<{ items: { id: string }[] }>(["items"]);
    preSettleItemIds.current = cachedItems ? cachedItems.items.map((i) => i.id) : null;
    setSettleState({ status: "pending" });
    const inputLog = live.inputLog.map((e) => ({ tMs: e.tMs, moveId: e.moveId }));
    const claimedHash = live.result.endStateHash;
    const claimedOutcome = live.result.outcome;
    settle({
      data: {
        zoneId,
        stageIndex,
        seed,
        inputLog,
        claimedHash,
        claimedOutcome,
        // Der Server leitet den Score ohnehin selbst aus dem inputLog ab und
        // ignoriert diesen Wert — trotzdem nach derselben Regel schicken.
        activeScore: Math.min(
          100,
          countScoredCasts(inputLog, playerMoveCds) * ACTIVE_SCORE_PER_CAST,
        ),
        autoCastEnabled: frozenAutoCast,
        // Zweite Sicherung neben der Sperre vor dem Kampf: Kommt ein Settle
        // trotzdem aus einem veralteten Tab, nennt der Server den Grund beim
        // Namen statt „settle mismatch".
        kernelVersion: HASH_VERSION,
        // Muss mit: Der Server erzwingt sonst die Frontier-Sperre und rechnet
        // einen anderen Kampf nach. Er beachtet den Wert nur im Dev-Build.
        // Der EINGEFRORENE Wert, nicht der aktuelle Schalterstand.
        devFrontierAutoCast: frozenDevFrontier,
      },
    })
      .then((r) => {
        setSettleState({
          status: "done",
          outcome: r.outcome,
          xp: r.xp,
          gold: r.gold,
          itemsCreated: r.itemsCreated,
          autoSalvaged: r.autoSalvaged,
          autoSalvageGold: r.autoSalvageGold,
          activeBonusPct: r.activeBonusPct,
          isFrontier: r.isFrontier,
          rewardMult: r.rewardMult,
        });
        // Fired here, at the actual win, like the legacy idle fight did — the
        // polling fallback in BackgroundPolling only evaluates every 5 minutes.
        if (r.outcome === "win") triggerTutorial("first_battle_won");
        // Genau im Moment des Funds. Vorher hing das an `BackgroundPolling`,
        // das den GECACHTEN `["items"]`-Eintrag prüfte — der Hinweis „Erstes
        // Loot!" erschien also irgendwann nach einem Inventar-Besuch, auf
        // welchem Bildschirm man dann gerade war.
        if (r.equipmentDropped > 0) triggerTutorial("first_loot_coach");
        if (r.eggsDropped > 0) triggerTutorial("first_egg_coach");
        // Learning is the only source of moves now, so say so when it happens.
        if (r.levelUp) {
          // Die Ultimate kommt über denselben Weg (Slot 7), verdient aber
          // mehr als eine Zeile Fließtext — sie ist der seltenste Fund im Spiel.
          const learned = r.levelUp.newMoves.map((m) =>
            m.isUltimate ? `🌟 ${m.name} (Slot ${m.slot})` : `${m.name} (Slot ${m.slot})`,
          );
          toast.success(
            learned.length > 0
              ? `Level ${r.levelUp.toLevel}! Neue Attacke: ${learned.join(", ")}`
              : `Level ${r.levelUp.toLevel}!`,
          );
          // Der Toast nennt nur den Namen. Was die Attacke im Zonenkampf kann
          // — und die Entscheidung bei vollen Slots — gehört ins Modal.
          if (r.levelUp.newMoves.length > 0 || r.levelUp.moveOfferOpen) {
            setLearnResult({
              learned: r.levelUp.newMoves,
              offerOpen: r.levelUp.moveOfferOpen,
            });
          }
          // Ohne das steht die frisch gelernte Attacke bis zu 60 s nicht in
          // der Slot-Leiste (`moveSlotsQ`, Key ["monster-moves"], staleTime
          // 60 s) — und der Angebots-Dialog fände sein `pending` nicht.
          void qc.invalidateQueries({ queryKey: ["monster-moves"] });
        }
        // Erfolge zahlen Gold, das aktiv abgeholt werden muss — ohne Hinweis
        // merkt der Spieler nie, dass etwas auf ihn wartet.
        // Wenn eine Belohnung nicht gebucht werden konnte, erfaehrt der
        // Spieler das. Frueher riss so ein Fehlschlag den ganzen Kampf; jetzt
        // laeuft der Rest durch, aber still verschwinden darf er nicht.
        if (r.rewardFailures.length > 0) {
          toast.warning(
            `Nicht alles konnte gebucht werden: ${r.rewardFailures.join(", ")}. Der Rest ist angekommen.`,
          );
        }
        if (r.achievementsUnlocked.length > 0) {
          const titles = r.achievementsUnlocked
            .map((id) => ACHIEVEMENT_MAP[id])
            .filter(Boolean)
            .map((a) => `${achievementTitle(a)} (+${a.reward_gold} Gold)`);
          if (titles.length > 0) {
            toast.success(`Erfolg freigeschaltet: ${titles.join(", ")} — unter „Erfolge" abholen.`);
          }
          void qc.invalidateQueries({ queryKey: ["achievements"] });
        }
        // Der Fortschritt kommt fertig aus der Antwort — direkt in den Cache,
        // nicht per invalidate. Zwei Gruende: er entscheidet ueber `isFrontier`,
        // und der Server erzwingt im Frontier `autoCastEnabled = false`. Nach
        // einer Frontier-Niederlage spult der Server auf den Checkpoint zurueck;
        // bliebe der Client auf dem alten Stand, hielte er die Checkpoint-Stage
        // faelschlich fuer Frontier, rechnete mit Auto-Cast an, der Server mit
        // aus — und der naechste SIEG risse mit "settle mismatch". Schreiben
        // statt invalidieren spart ausserdem die Server-Runde.
        qc.setQueryData(["battle-v2", "progress"], r.progress);
        // ⚠️ Das eigene Xenomon MUSS hier mit aufgefrischt werden, sonst
        // kaempft der naechste Kampf mit veralteten Werten und reisst mit
        // "settle mismatch". Genau das ist Sammy am 09.09.2026 passiert:
        // Sein Browser rechnete mit Level 2 (ATK 23/DEF 16/HP 190), der
        // Server mit Level 3 (ATK 25/DEF 17/HP 200) — nachgerechnet ergibt
        // Level 2 exakt seine Pruefsumme 2f5a16d6/Niederlage und Level 3
        // exakt 85bef7e2/Sieg.
        // Der Snapshot haengt an ["battle-v2","monster"] mit staleTime 60 s
        // und ohne Refetch bei Fokus oder Reconnect; einziger Auffrischer
        // war bis dahin advance() ("Weiter →"/"Erneut versuchen"). Wer
        // "Zurueck" oder "Zur Frontier →" nimmt, kaempfte mit den alten
        // Werten weiter.
        // BEDINGUNGSLOS, nicht nur bei r.levelUp: Auch Auto-Salvage, Beute
        // und Erfolge fassen die monsters-Zeile an. Ein laufender Kampf
        // stoert das nicht — useLiveCombat friert seine Eingabe beim
        // Mounten ein.
        void qc.invalidateQueries({ queryKey: ["battle-v2", "monster"] });
        // Nach dem Settle stehen Evolutions-Flag, Level/Gold und die neu
        // erzeugten Items (inkl. Eier/Apex) serverseitig — die Caches der
        // anderen Screens sonst bis zu 60 s alt (MonsterLifecycle).
        void qc.invalidateQueries({ queryKey: ["my-monster"] });
        if (r.itemsCreated > 0) {
          void qc.invalidateQueries({ queryKey: ["items"] });
          void qc.invalidateQueries({ queryKey: ["eggs"] });
        }
        const trainingSuffix = r.isFrontier ? "" : " · Training 50%";
        const bonusSuffix =
          r.activeBonusPct > 0 ? ` · Aktiv-Bonus +${de(r.activeBonusPct * 100, 1)} %` : "";
        // Was der Auto-Salvage weggeräumt hat, gehört in den Toast — sonst
        // wirkt der Kampf ärmer, als er war, und der Spieler sucht im
        // Inventar nach Fundstücken, die es gar nicht mehr gibt.
        const salvageSuffix =
          r.autoSalvaged > 0
            ? ` · ${r.autoSalvaged} verwertet (+${r.autoSalvageGold} Gold)`
            : "";
        toast.success(
          claimedOutcome === "win"
            ? `Sieg! +${r.xp} XP · +${r.gold} Gold${r.itemsCreated ? ` · ${r.itemsCreated} Beute` : ""}${salvageSuffix}${bonusSuffix}${trainingSuffix}`
            : "Niederlage abgerechnet.",
        );
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setSettleState({ status: "error", message: msg });
        // Auch im Fehlerfall auffrischen — besonders beim Mismatch selbst.
        // Sonst bleibt genau der veraltete Cache stehen, der den Riss
        // ausgeloest hat, und der naechste Kampf reisst wieder. Der
        // Fehlerbildschirm bot bis zum 09.09.2026 nur "Zurueck" an, und
        // "Zurueck" laedt nichts nach: Der Fehler heilte sich erst nach
        // 60 s oder ueber einen Umweg durch den Stall.
        void qc.invalidateQueries({ queryKey: ["battle-v2"] });
        // Melden, nicht nur anzeigen: Ein fehlgeschlagener Settle kostet den
        // Spieler einen ausgefochtenen Kampf. Genau diese Fehlerklasse tauchte
        // im Browser-Sentry nie auf, weil sie hier abgefangen wird und damit
        // kein `window.onerror` auslöst (Sammy, 07.09.2026).
        void import("@/lib/sentry-browser").then(({ captureClientException }) =>
          captureClientException(err, { bereich: "settle", zoneId, stageIndex, seed }),
        );
        toast.error(`Settle-Fehler: ${msg}`);
      });
  }, [live.result, live.inputLog, seed, zoneId, stageIndex, settle, frozenAutoCast, frozenDevFrontier, settleState.status, qc, playerMoveCds]);


  // Auto-advance countdown: only after a successful settle of a WIN.
  // `lootHold`: In der Beute liegt ein anlegbares besseres Teil. Der
  // Countdown pausiert, bis der Spieler entschieden hat — der Weiter-Schritt
  // remountet die Arena und nähme den „Anlegen"-Knopf sonst nach 2 s vom
  // Schirm. Sobald das Teil angelegt (oder die Liste leer) ist, meldet
  // LootReveal `false` und der Countdown läuft an.
  const [lootHold, setLootHold] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const advancingRef = useRef(false);
  useEffect(() => {
    if (settleState.status !== "done") return;
    if (!autoAdvance) return;
    if (settleState.outcome !== "win") return;
    if (advancingRef.current) return;
    // Solange das Lern-Popup offen ist, wird nicht weitergeschaltet: Der
    // Weiter-Schritt remountet die Arena (key-Wechsel in BattlePage) und
    // nähme das Popup mitsamt einer offenen Attacken-Entscheidung vom Schirm.
    // Nach dem Schließen läuft dieser Effekt erneut und der Countdown startet.
    if (learnResult) return;
    if (lootHold) return;
    setCountdown(2);
    const iv = window.setInterval(() => {
      setCountdown((c) => (c === null ? null : c - 1));
    }, 1000);
    const to = window.setTimeout(() => {
      window.clearInterval(iv);
      if (advancingRef.current) return;
      advancingRef.current = true;
      void onAdvance();
    }, 2000);
    return () => {
      window.clearInterval(iv);
      window.clearTimeout(to);
    };
  }, [settleState, autoAdvance, onAdvance, learnResult, lootHold]);

  const cancelAutoAdvance = useCallback(() => {
    setCountdown(null);
    setAutoAdvance(false);
  }, [setAutoAdvance]);

  const manualAdvance = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    void onAdvance();
  }, [onAdvance]);

  // Compact log slice — newest first (last 120 events reversed).
  const [logSlice, setLogSlice] = useState<typeof live.events>([]);
  useEffect(() => {
    setLogSlice(live.events.slice(-120).slice().reverse());
  }, [snap.logCount, live.events]);


  // 1-gegen-1: es lebt immer höchstens ein gespawnter Gegner — genau der, auf
  // den der Kernel zielt (`enemies.find(e => e.spawned && e.hp > 0)` in
  // simulate.ts). Alle übrigen warten ungespawnt in der Warteschlange.
  const activeEnemy =
    snap.enemies.find((e) => e.spawned && e.hp > 0) ??
    // Stage vorbei, alle tot: den ZULETZT gefallenen zeigen. Vorher stand hier
    // schlicht `enemies[0]` — nach dem Sieg sprang die Karte damit zurück auf
    // den ERSTEN Gegner der Stage, der längst gefallen war (Stefan, 09.09.2026:
    // „wenn man gegen mehrere Gegner kämpft, wird am Ende der Stage wieder der
    // erste Gegner angezeigt"). Die Liste steht in Spawn-Reihenfolge, der
    // letzte gespawnte ist also der zuletzt bekämpfte.
    [...snap.enemies].reverse().find((e) => e.spawned) ??
    snap.enemies[0];
  const queuedEnemies = snap.enemies.filter((e) => !e.spawned);
  const resolveEnemyName = useCallback(
    (id: string): string => {
      const slug = id.split("#")[0];
      const boss = bossNameBySlug[slug];
      if (boss) return boss;
      const raw = enemyLabel(id);
      // Ugly slug-fallback ("Z2-Gegner") → "Zonenboss (Zone …)".
      if (/^Z\d+-Gegner$/i.test(raw)) {
        return zoneNameDe ? `Zonenboss · ${zoneNameDe}` : "Zonenboss";
      }
      return raw;
    },
    [bossNameBySlug, zoneNameDe],
  );
  const activeEnemyName = resolveEnemyName(activeEnemy.id);
  const activeEnemySlug = activeEnemy.id.split("#")[0];
  /**
   * Tier für Kartenfarbe und Pill.
   *
   * ⚠️ Die Boss-Erkennung darf NICHT aus dem Slug kommen: Boss-Slugs
   * (`z3-helxar`) tragen kein Tier-Segment, `slug.split("-")[1]` gab für jeden
   * Boss „normal" — genau der Fehler, der am 01.09.2026 die Boss-Beute
   * halbiert hat (§6a). Maßgeblich ist derselbe Katalog, aus dem auch der
   * Boss-Name kommt.
   */
  const enemyTierOf = useCallback(
    (id: string): EnemyTier => {
      const slug = id.split("#")[0];
      if (bossNameBySlug[slug]) return "boss";
      return slug.split("-")[1] === "elite" ? "elite" : "normal";
    },
    [bossNameBySlug],
  );
  const activeEnemyTier = enemyTierOf(activeEnemy.id);
  const activeEnemyFull = useMemo(
    () => stage.enemies.find((e) => e.id === activeEnemy.id) ?? null,
    [stage.enemies, activeEnemy.id],
  );
  // Kann dieser Gegner überhaupt ausholen? Zonen mit `use_real_moves = false`
  // bekommen den Legacy-`AUTO_MOVE`/`basic` mit castMs 0 (enemy-moves.ts), und
  // auch der Gegner-Grundangriff castet nicht — dort darf die Cast-Zeile gar
  // nicht erst Platz belegen.
  const activeEnemyCanCast = useMemo(
    () => (activeEnemyFull?.moves ?? []).some((m) => castMsOf(m) > 0),
    [activeEnemyFull],
  );
  /**
   * Der Typvorteil hängt seit dem 07.09.2026 an der ATTACKE, nicht am Kämpfer
   * — ein Xenomon trägt Attacken verschiedener Typen. Eine einzelne Pille auf
   * der Gegnerkarte kann das nicht mehr sagen; sie ist deshalb weg (Stefan,
   * 09.09.2026), und jede Slot-Kachel trägt ihren eigenen Punkt.
   *
   * Gerechnet wird NIE hier, sondern in `typVorteilFuerAnzeige`: Die benutzt
   * `attackTypeFor` und `typeMultiplier` aus dem Kernel und kennt alle drei
   * Tore (Attacken-Typ, `power <= 0`, `ignoreTypeMatrix`). Wer hier selbst
   * rechnet, baut die nächste Lüge ein.
   */
  const vorteilFuer = useCallback(
    (m: { power: number; moveType?: string | null; isIntrinsic?: boolean }) =>
      typVorteilFuerAnzeige(
        m,
        player.type as CreatureType,
        (activeEnemyFull?.type as CreatureType | undefined) ?? null,
        stage.ignoreTypeMatrix === true,
      ),
    [player.type, activeEnemyFull, stage.ignoreTypeMatrix],
  );
  const [showEnemyInfo, setShowEnemyInfo] = useState(false);
  const [showPlayerInfo, setShowPlayerInfo] = useState(false);
  // Unter 768px liegen Schalter, Aktiv-Bonus und Log im Bottom-Sheet
  // „Kampf-Details" (siehe kampfDetails weiter unten).
  const isMobile = useIsMobile();

  /**
   * Am Desktop mitscrollen: ans Ende, wenn die Stage vorbei ist (dort steht das
   * Ergebnis mit Beute und Belohnung), und zurueck nach oben, sobald die
   * naechste beginnt (Stefan, 09.09.2026).
   *
   * Nur am Desktop — am Handy liegt das Ergebnis ohnehin im Blick, und ein
   * springender Bildschirm waere dort stoerender als hilfreich.
   */
  const ergebnisRef = useRef<HTMLDivElement | null>(null);
  const sanft = (): ScrollBehavior => {
    try {
      // Wer Bewegung reduziert haben will, bekommt den Sprung ohne Animation.
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    } catch {
      return "auto";
    }
  };
  useEffect(() => {
    if (isMobile || !snap.done) return;
    ergebnisRef.current?.scrollIntoView({ behavior: sanft(), block: "end" });
  }, [isMobile, snap.done]);
  useEffect(() => {
    if (isMobile) return;
    try {
      window.scrollTo({ top: 0, behavior: sanft() });
    } catch {
      /* Kein Browser — dann eben nicht. */
    }
  }, [isMobile, stage.stageId]);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Kampf-Momente: Schadenszahlen und Ruckeln je Seite. Reine Anzeige — beide
  // Haken lesen nur das Ereignis-Log, das der Kernel ohnehin schreibt.
  const gegnerTreffer = useHitFloaters(live.events, snap.logCount, activeEnemy.id);
  const spielerTreffer = useHitFloaters(live.events, snap.logCount, player.id);

  // Collect all slugs currently on the stage for a single lookup call.
  const stageSlugs = useMemo(() => {
    const set = new Set<string>();
    for (const e of stage.enemies) set.add(e.id.split("#")[0]);
    return Array.from(set);
  }, [stage.enemies]);
  const fetchImages = useServerFn(getBattleEnemyImages);
  const imagesQ = useQuery({
    queryKey: ["battle-v2", "enemy-images", stageSlugs.slice().sort().join(",")],
    queryFn: () => fetchImages({ data: { slugs: stageSlugs } }),
    enabled: stageSlugs.length > 0,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
  const activeEnemyImage = imagesQ.data?.[activeEnemySlug] ?? null;

  const exportLog = useCallback(() => {
    const payload = {
      seed,
      zoneId,
      stageIndex,
      autoCastEnabled: frozenAutoCast,
      inputLog: live.inputLog,
      result: live.result,
      events: live.events,
    };
    const text = JSON.stringify(payload, null, 2);
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(text).then(
        () => toast.success("Log in Zwischenablage kopiert"),
        () => toast.error("Kopieren fehlgeschlagen"),
      );
    } else {
      console.log(text);
      toast.info("Log in Console geloggt");
    }
  }, [seed, zoneId, stageIndex, frozenAutoCast, live.inputLog, live.result, live.events]);

  // Intrinsischer Grundangriff: nicht slot-belegbar, daher eine eigene Kachel
  // vor den Slots statt eines Slot-Eintrags. Der Kernel nimmt manuelle Casts
  // auf ihn an (validiert nur gegen player.moves), im reinen Manual-Modus ist
  // die Kachel der einzige Weg, ihn zu feuern.
  const slotMoves = useMemo(() => player.moves.filter((m) => !m.isIntrinsic), [player.moves]);
  // Gibt es überhaupt etwas zu tippen? Ohne Slot-Attacke ist die Frontier-
  // Regel wirkungslos (`pickAutoMove` hat mit und ohne Auto-Cast nur den
  // Grundangriff als Kandidaten) — dann wird sie auch nicht angekündigt und
  // der Schalter verschwindet. Siehe FRONTIER_MANUAL_*.
  const hasSlotMoves = slotMoves.length > 0;
  const showFrontierManual = isFrontier && hasSlotMoves;

  /**
   * Was der Aktiv-Bonus mit DIESEM Move-Set überhaupt hergibt.
   *
   * `maxScoredCastsWithin` ist die Obergrenze bei perfektem Tippen; erreicht
   * werden kann sie nur ohne Auto-Cast (also im Frontier), im Training feuert
   * der Automatismus einen Teil der Casts selbst und die zählen nicht. Der
   * Deckel liegt bei ACTIVE_CASTS_FOR_CAP Casts — ob und wann das Set ihn
   * erreichen kann, steht unter der Anzeige.
   */
  const capReachableAtMs = useMemo(
    () => msToActiveBonusCap(slotMoves, stage.maxDurationMs),
    [slotMoves, stage.maxDurationMs],
  );
  const maxCastsInStage = useMemo(
    () => maxScoredCastsWithin(slotMoves, stage.maxDurationMs),
    [slotMoves, stage.maxDurationMs],
  );

  const intrinsicMove = player.moves.find((m) => m.isIntrinsic);
  const intrinsicCdMs = intrinsicMove ? (snap.player.cooldowns[intrinsicMove.id] ?? 0) : 0;

  // ALLE laufenden Cast-Phasen des Spielers (`moveId` → Restzeit). Seit C1-B
  // holt jeder Move unabhängig aus: Gesperrt wird deshalb genau die Kachel des
  // ⚠️ Seit dem 10.09.2026 sperrt ein laufender Cast die GANZE Leiste (bis
  // auf den Basis-Angriff, der den Kanal nicht belegt). Der Cooldown des
  // ausholenden Moves steht dabei noch auf 0, `cdMs > 0` allein würde seine
  // Kachel also offen lassen und den Tap ins Leere laufen lassen.
  const playerCasting = snap.player.casting;
  /**
   * Ist der eine Kanal gerade belegt?
   *
   * ⚠️ Seit dem 10.09.2026 holt nur EINE Attacke gleichzeitig aus, und nach
   * einer Unterbrechung bleibt der Kanal zusätzlich für den Rest der
   * abgebrochenen Ladephase gesperrt. Beides muss die Leiste zeigen, sonst
   * sehen Kacheln antippbar aus, deren Tap der Kernel wegwirft — und der
   * Spieler hält es für einen Defekt.
   */
  const kanalBelegt =
    Object.values(playerCasting).some((c) => c !== null && c !== undefined) ||
    snap.player.castBlockedMs > 0;

  /**
   * Kachel → Move über die ECHTE Slotnummer, nicht über die Listenposition.
   *
   * Die Sperr-Logik unten ist slotnummernbasiert (`SLOT_UNLOCK[slot]`), die
   * Zuordnung war positionsbasiert (`slotMoves[i]`). `forgetMove`
   * (moves.functions.ts) löscht eine `monster_moves`-Zeile, ohne die folgenden
   * Slots aufzurücken — nach einer Lücke rutschte jeder folgende Move eine
   * Kachel nach vorn: Kachel 2 zeigte und castete den Slot-3-Move, Kachel 4
   * stand auf "—", obwohl dort ein Move liegt.
   *
   * Die Slotnummer steht NICHT im Kampf-Snapshot: `MoveSnapshot`
   * (combat-core/types.ts) kennt nur `slot1?: boolean`. Sie kommt deshalb aus
   * `listMonsterMoves` (Query-Key `["monster-moves"]`, siehe BattlePage).
   * Fallback auf die alte Zuordnung, solange/falls die Slot-Daten nicht zu den
   * Moves des Snapshots passen (Query noch nicht geladen, zwischenzeitlich
   * anderes Xenomon aktiv) — lieber die bekannte Näherung als eine halb leere
   * Leiste.
   */
  const moveBySlot = useMemo(() => {
    const bySlot = new Map<number, MoveSnapshot>();
    let resolved = 0;
    for (const m of slotMoves) {
      const s = slotByMoveId.get(m.id) ?? (m.slot1 ? 1 : undefined);
      if (s === undefined || s < 1 || s > MAX_MOVE_SLOTS || bySlot.has(s)) continue;
      bySlot.set(s, m);
      resolved += 1;
    }
    if (resolved === slotMoves.length) return bySlot;
    const fallback = new Map<number, MoveSnapshot>();
    slotMoves.forEach((m, i) => fallback.set(i + 1, m));
    return fallback;
  }, [slotMoves, slotByMoveId]);

  /**
   * Schalter, Aktiv-Bonus und Log — auf dem Handy der Inhalt des Bottom-
   * Sheets „Kampf-Details", auf dem Desktop der untere Teil der Seite.
   * Funktionsparität ist Pflicht: Es wird nichts weggelassen, nur verlegt.
   */
  const kampfDetails = (
    <>
      {/* Die Erklaerung steht seit dem 09.09.2026 hier statt ueber dem Kampf:
          Sie wird einmal gelesen und kostete darueber auf JEDEM Bildschirm
          Platz — auf 390x844 war der Kampf-Screen dadurch nicht mehr ohne
          Scrollen zu bedienen (Stefan). Das Design-Handoff sieht genau dafuer
          das Bottom-Sheet vor: Sekundaeres wird verlegt, nicht weggelassen. */}
      <HowItWorks />

      {/* Controls. Schmal (Bottom-Sheet) untereinander, ab `sm` wie bisher in
          einer Zeile — im Sheet standen die Schalter sonst als vierzeilige
          Wortsäule neben den Knöpfen. */}
      <div className="flex flex-col gap-3 text-xs sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <div className="flex flex-col gap-2 sm:gap-1">
          {/* Ohne Slot-Attacke ist der Schalter folgenlos: Der Auto-Cast hat in
              beiden Stellungen nur den Grundangriff als Kandidaten, der Kampf
              läuft identisch. Dann gar nichts zeigen, statt eine Wahl und ein
              „Manuell: Kachel antippen" zu behaupten, die es nicht gibt. */}
          {hasSlotMoves && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* Im Frontier ist das ein ANZEIGE-Zustand, kein Schalter: der
                Server rechnet dort mit autoCastEnabled=false nach, und ein
                Toggle würde als `__autocast:on` im inputLog landen, den der
                Server wegfiltert → Hash-Divergenz. Im Training bleibt der
                Schalter voll bedienbar (auch mitten im Kampf). */}
            <label
              className={`flex min-h-6 items-center gap-2 ${frontierSperre ? "opacity-60" : ""}`}
              title={frontierSperre ? FRONTIER_MANUAL_TIP : undefined}
            >
              <input
                type="checkbox"
                checked={frontierSperre ? false : snap.done ? autoCast : snap.autoCastEnabled}
                disabled={frontierSperre}
                onChange={(e) => {
                  if (frontierSperre) return;
                  setAutoCast(e.target.checked);
                  if (!snap.done) live.setAutoCast(e.target.checked);
                }}
              />
              <span>
                Auto-Cast
                {frontierSperre ? " · auf neuen Stages aus" : ""}
                {isFrontier && !frontierSperre ? " · Testschalter" : ""}
              </span>
            </label>
            {!snap.done && !snap.autoCastEnabled && (
              <span className="order-last w-full text-[10px] text-amber-300 sm:order-none sm:w-auto">
                {isFrontier
                  ? "Deine Slot-Attacken tippst du selbst — der Basis-Angriff läuft."
                  : "Manuell: Kachel antippen, um anzugreifen (der Basis-Angriff läuft weiter)."}
              </span>
            )}
            <InfoTip text={showFrontierManual ? FRONTIER_MANUAL_TIP : AUTO_CAST_TIP}>
              <span className={INFO_DOT_CLASS} aria-label="Auto-Cast erklärt">
                ⓘ
              </span>
            </InfoTip>
          </div>
          )}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={autoAdvance}
                onChange={(e) => setAutoAdvance(e.target.checked)}
              />
              <span>Auto-Fortschritt</span>
            </label>
            <InfoTip text="An: nach Sieg automatisch nächste Stage (2s Countdown, abbrechbar).">
              <span className={INFO_DOT_CLASS} aria-label="Auto-Fortschritt erklärt">
                ⓘ
              </span>
            </InfoTip>
          </div>
        </div>
        {/* Hier standen bis zum 07.09.2026 „Pause/Fortsetzen" und „Verlassen".
            Sammy: „Das hat keine Verwendung mehr und Pause ist nur zwischen den
            Stages möglich" — die einzige Pause, die zählt, ist der abbrechbare
            Countdown des Auto-Fortschritts. Aus dem Kampf heraus führen weiter
            die Knöpfe der Ergebnis-Ansicht. Der Kampf startet ohnehin von
            selbst (`autoStart: true`); `pause`/`start` bleiben im Hook, haben
            damit aber keinen Aufrufer mehr in der Oberfläche. */}
      </div>

      {/* Active-play meter. Die Frontier-Regel macht den Bonus überhaupt erst
          spielbar (im Training feuert der Auto-Cast einen Teil der Casts
          selbst), sie macht ihn aber NICHT leicht: Der Deckel hängt am
          Move-Set, nicht am Fleiß. Deshalb steht unter dem Balken die aus dem
          Snapshot gerechnete Obergrenze statt einer Aufforderung. */}
      <div className="text-[10px] text-muted-foreground">
        <div className="flex items-center justify-between gap-2">
          <InfoTip text={ACTIVE_BONUS_TIP}>
            <span>Aktiv-Bonus ⓘ</span>
          </InfoTip>
          <span className="font-mono tabular-nums">
            {Math.min(scoredCasts, ACTIVE_CASTS_FOR_CAP)}/{ACTIVE_CASTS_FOR_CAP} Casts · +
            {de(activeBonusPctFor(scoredCasts), 1)} %
          </span>
        </div>
        <div className="mt-1 h-1 w-full overflow-hidden rounded bg-primary/10">
          <div
            className="h-full bg-primary/60 transition-all"
            style={{ width: `${Math.min(100, scoredCasts * ACTIVE_SCORE_PER_CAST)}%` }}
          />
        </div>
        {/* Was hier steht, muss der Kernel auch hergeben — deshalb keine
            Aufforderung „tippen, tippen, tippen", sondern die tatsächlich
            erreichbare Zahl aus DIESEM Move-Set (maxScoredCastsWithin).

            Der Basis-Angriff wird bewusst NICHT als Bonusquelle beworben:
            Der Auto-Cast startet ihn in dem Tick, in dem sein Cooldown 0
            erreicht (bei ausgeschaltetem Auto-Cast ist er sogar der einzige
            Kandidat), und mit castMs 0 fallen Treffer und Cooldown in
            denselben Tick. Seine Kachel ist `disabled={intrinsicCdMs > 0}`
            und deshalb praktisch nie im bereiten Zustand zu sehen;
            automatisch gefeuerte Casts schreiben keinen inputLog-Eintrag.
            Server-Regel in settle.functions.ts, Spiegel in countScoredCasts. */}
        <div className="mt-1">
          {!hasSlotMoves
            ? "Zählbar sind nur selbst getippte Slot-Attacken — dein Xenomon hat noch keine. Der Basis-Angriff feuert von allein und zählt nicht; dieser Kampf bringt keinen Aktiv-Bonus."
            : capReachableAtMs === null
              ? `Mit deinen ${slotMoves.length} Attacken sind in einem Kampf höchstens ${maxCastsInStage} gewertete Casts drin (+${de(activeBonusPctFor(maxCastsInStage), 1)}${NBSP}%) — für die vollen +${ACTIVE_BONUS_MAX_PCT}${NBSP}% brauchst du mehr oder schnellere Attacken. Der Basis-Angriff zählt nie, er feuert von allein.`
              : `Für die vollen +${ACTIVE_BONUS_MAX_PCT}${NBSP}% brauchst du ${ACTIVE_CASTS_FOR_CAP} gewertete Casts — mit deinen ${slotMoves.length} Attacken frühestens nach ${de(capReachableAtMs / 1000, 0)}${NBSP}s Kampf. Der Basis-Angriff zählt nie, er feuert von allein.`}
          {hasSlotMoves &&
            (isFrontier
              ? " Hier kommt dir wenigstens kein Auto-Cast zuvor."
              : " Im Training zählen XP und Gold zusätzlich nur 50 %, und der Auto-Cast feuert einen Teil deiner Attacken selbst.")}
        </div>
      </div>

      {/* Log */}
      <div className="rounded border border-primary/20 bg-black/40 p-2 font-mono text-[10px]">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-muted-foreground">Log ({logSlice.length})</span>
          <Button size="sm" variant="ghost" onClick={exportLog} className="h-6 px-2 text-[10px]">
            Log exportieren
          </Button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {logSlice.map((ev, i) => {
            const line = formatCombatEvent(ev, player.id, playerLabel, resolveEnemyName, moveNameById);
            if (!line) return null;
            return (
              <div key={i} className={line.className}>
                <span className="text-muted-foreground">[{de(ev.t / 1000, 1)}s]</span>{" "}
                {line.text}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Gegnerkarte */}
      <BattleEnemyCard
        name={activeEnemyName}
        tier={activeEnemyTier}
        type={activeEnemy.type as CreatureType}
        level={activeEnemy.level}
        hp={activeEnemy.hp}
        hpMax={activeEnemy.hpMax}
        imageUrl={activeEnemyImage}
        queued={queuedEnemies.length}
        shake={gegnerTreffer.shaking}
        overlay={<FloatNumLayer floaters={gegnerTreffer.floaters} />}
        castRow={
          <EnemyCastRow
            casting={activeEnemy.casting}
            canCast={activeEnemyCanCast}
            nameOf={(moveId) => moveNameById.get(moveId) ?? null}
          />
        }
        info={
          <InfoToggle
            open={showEnemyInfo}
            onClick={() => setShowEnemyInfo((v) => !v)}
            label="Gegner-Info"
          />
        }
        details={
          showEnemyInfo && activeEnemyFull ? (
            <MonsterInfoPanel
              name={activeEnemyName}
              level={activeEnemyFull.level}
              type={activeEnemyFull.type as CreatureType}
              hp={activeEnemy.hp}
              hpMax={activeEnemy.hpMax}
              atk={activeEnemyFull.atk}
              def={activeEnemyFull.def}
              moves={activeEnemyFull.moves}
              opponentType={player.type as CreatureType}
              ignoreTypeMatrix={stage.ignoreTypeMatrix === true}
            />
          ) : null
        }
      />

      {/* Warteschlange: kommende Gegner, treten nacheinander an (1-gegen-1) */}
      {queuedEnemies.length > 0 && (
        <div>
          <div className="mb-1.5 flex items-center gap-1 font-display text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
            <InfoTip
              text={`Die Gegner treten nacheinander an — der nächste erscheint erst, wenn der aktuelle besiegt ist. Jeder Kill heilt dein Xenomon um ${Math.round(KILL_HEAL_PCT * 100)} % seiner maximalen HP.`}
            >
              <span>Als Nächstes ⓘ</span>
            </InfoTip>
          </div>
          {/* Reihe statt Liste: Bei vier Gegnern kosteten die vollen Zeilen
              rund 170 Pixel — auf 390×844 der Unterschied zwischen Scrollen und
              nicht. Läuft die Reihe über, scrollt sie seitwärts weiter, statt
              den Kampf-Screen in die Höhe zu treiben. */}
          <div className="flex gap-1.5 overflow-x-auto pb-0.5">
            {queuedEnemies.map((e) => (
              <QueuedEnemyCard
                key={e.id}
                name={resolveEnemyName(e.id)}
                type={e.type as CreatureType}
                level={e.level}
                imageUrl={imagesQ.data?.[e.id.split("#")[0]] ?? null}
              />
            ))}
          </div>
        </div>
      )}

      {/* Eigenes Xenomon */}
      <BattlePlayerCard
        name={player.displayName ?? playerLabel}
        type={player.type as CreatureType}
        level={player.level}
        hp={snap.player.hp}
        hpMax={snap.player.hpMax}
        imageUrl={player.imageUrl ?? null}
        shake={spielerTreffer.shaking}
        overlay={<FloatNumLayer floaters={spielerTreffer.floaters} />}
        info={
          <InfoToggle
            open={showPlayerInfo}
            onClick={() => setShowPlayerInfo((v) => !v)}
            label="Monster-Info"
          />
        }
        footer={
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            <InfoTip text={COMBO_TIP}>
              <span className={snap.player.combo > 0 ? "font-extrabold text-hp-ally" : undefined}>
                Combo {snap.player.combo}/{COMBO_MAX} · +
                {Math.round(snap.player.combo * COMBO_DMG_PER_STACK * 100)} % Schaden ⓘ
              </span>
            </InfoTip>
            <span className="tabular-nums">t {de(snap.now / 1000, 1)}s</span>
          </div>
        }
        details={
          showPlayerInfo ? (
            <MonsterInfoPanel
              name={player.displayName ?? playerLabel}
              level={player.level}
              type={player.type as CreatureType}
              hp={snap.player.hp}
              hpMax={snap.player.hpMax}
              atk={player.atk}
              def={player.def}
              moves={player.moves}
              opponentType={activeEnemyFull ? (activeEnemyFull.type as CreatureType) : undefined}
              ignoreTypeMatrix={stage.ignoreTypeMatrix === true}
            />
          ) : null
        }
      />

      {/* Slot bar — klebt während des Kampfs am unteren Rand: der Gegner-Block
          darüber wächst mit jedem Spawn, ohne sticky verschöbe das die Kacheln
          unter dem zielenden Finger. Nach dem Kampf wieder im Fluss, damit sie
          das Ergebnis-Panel nicht überdeckt. */}
      <div
        className={`-mx-3 border-t-2 border-ink bg-background/95 px-3 pb-2 pt-2 backdrop-blur ${
          snap.done ? "" : "sticky bottom-0 z-20"
        }`}
      >
        {/* Die Frontier-Regel steht direkt über den Kacheln, an denen sie
            hängt — im Kampf schaut niemand mehr nach oben. Nach dem Kampf
            weg: dort erklärt das Ergebnis-Panel weiter. */}
        {showFrontierManual && !snap.done && (
          <div className="mb-1 rounded border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-100">
            <span className="font-semibold">{FRONTIER_MANUAL_HEADLINE}</span>{" "}
            <InfoTip text={FRONTIER_MANUAL_TIP}>
              <span>Basis-Angriff läuft automatisch, deine Slot-Attacken tippst du selbst ⓘ</span>
            </InfoTip>
          </div>
        )}
        {/* Offenes Lern-Angebot — im Kampf ist die Slot-Leiste die einzige
            Stelle, die dauerhaft im Blick ist. Führt in denselben Dialog wie
            nach einem Level-Up. */}
        {pendingOfferName && (
          <button
            type="button"
            onClick={onOpenOffer}
            className="mb-1 w-full rounded border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-left text-[10px] text-amber-100 hover:bg-amber-500/20"
          >
            <span className="font-semibold">Offene Attacken-Entscheidung:</span>{" "}
            {pendingOfferName} wartet — bis dahin kommt kein weiteres Angebot. Antippen zum
            Entscheiden →
          </button>
        )}
        <div className="mb-1.5 flex items-center justify-between gap-2 font-display text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
          <InfoTip text={SLOTS_TIP}>
            <span>Slots ⓘ</span>
          </InfoTip>
          {isMobile ? (
            /* Der einzige Weg zu Schaltern, Aktiv-Bonus und Log auf dem Handy —
               deshalb trägt er den Bonus-Stand gleich mit, sonst müsste man das
               Sheet öffnen, nur um nachzusehen.

               44 px hoch (min-h-11), OHNE `sm`-Verkleinerung: Er wird nur auf
               dem Handy gerendert und trägt dort allein die Funktionsparität
               zum Desktop. Mit 32 px lag er unter der Touch-Marke des Kanons —
               und direkt darunter beginnt die Slot-Leiste, wo ein Fehlgriff
               mitten im Kampf eine Attacke auslöst statt das Sheet zu öffnen. */
            <button
              type="button"
              onClick={() => setDetailsOpen(true)}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full border-2 border-ink bg-secondary px-2.5 font-display text-[10px] uppercase tracking-[0.1em] text-foreground"
            >
              <ScrollText className="h-3.5 w-3.5" strokeWidth={2.5} />
              Log · Bonus {Math.min(scoredCasts, ACTIVE_CASTS_FOR_CAP)}/{ACTIVE_CASTS_FOR_CAP}
            </button>
          ) : (
            <InfoTip text={CAST_TIP}>
              <span>Ausholen ⓘ</span>
            </InfoTip>
          )}
        </div>
        {/* Schmale Viewports: 4 Spalten in zwei Reihen statt 8 in einer —
            bei 375px blieben sonst ~40px pro Kachel, unter der 44px-Marke
            für Touch-Ziele. */}
        <div
          className={`grid grid-cols-4 gap-2 ${intrinsicMove ? "sm:grid-cols-8" : "sm:grid-cols-7"}`}
        >
          {intrinsicMove && (
            <InfoTip
              text={
                <div className="text-left">
                  <div className="font-semibold">{moveDisplayName(intrinsicMove)}</div>
                  <div className="mt-1 text-[10px] opacity-90">
                    Fest eingebauter Basis-Angriff — kein Slot, nicht tauschbar. Mit Auto-Cast
                    feuert er, wenn kein anderer Move bereit ist; antippbar ist er, sobald sein
                    eigener Cooldown abgelaufen ist — auch dann, wenn dein Xenomon gerade für
                    andere Attacken ausholt. Ist er der einzige Kandidat (Auto-Cast aus oder noch
                    keine Slot-Attacke gelernt), startet ihn der Automatismus in genau dem Moment,
                    in dem er bereit wird — dann bleibt die Kachel gesperrt, und für den
                    Aktiv-Bonus taugt sie nicht.
                  </div>
                  <div className="text-[10px] opacity-90">
                    Power ×{de(intrinsicMove.power, 2)}
                  </div>
                  <div className="text-[10px] opacity-90">{castCycleText(intrinsicMove)}</div>
                </div>
              }
            >
              {/* KEIN Cast-Zustand: der Grundangriff trägt fest castMs 0
                  (snapshot.ts) und geht deshalb nie in die Cast-Phase. Er ist
                  damit auch nie durch sich selbst gesperrt, und das Ausholen
                  anderer Moves sperrt ihn seit C1-B ebenfalls nicht mehr: nur
                  sein eigener Cooldown zählt. */}
              <MoveTile
                kind="basis"
                vorteil={vorteilFuer(intrinsicMove)}
                name={moveDisplayName(intrinsicMove)}
                cooldownPct={Math.min(100, (intrinsicCdMs / intrinsicMove.cooldownMs) * 100)}
                disabled={intrinsicCdMs > 0 || snap.done}
                onClick={() => live.cast(intrinsicMove.id)}
              />
            </InfoTip>
          )}
          {Array.from({ length: MAX_MOVE_SLOTS }).map((_, i) => {
            const slot = i + 1;
            const move = moveBySlot.get(slot);
            // Deutsche Wörter für die Statuseffekte. Eine Attacke kann
            // MEHRERE tragen (Ultimates tun das), deshalb eine Liste. Ohne
            // Wort bleibt der Eintrag weg — flatMap statt filter, damit
            // TypeScript das auch sieht.
            const appliedStatuses = (move?.applies ?? []).flatMap((a) => {
              const basis = STATUS_LABEL[a.kind];
              if (!basis) return [];
              // Hier ist die Attacke bekannt — also darf der Schaden über Zeit
              // seinen richtigen Namen tragen statt des neutralen.
              const label =
                a.kind === "burn" ? { ...basis, name: dotBezeichnung(move?.moveType) } : basis;
              return [{ apply: a, label }];
            });
            // Freischaltstufe aus derselben Konstante wie das Move-Menü —
            // vorher prüfte das UI nur Slot 6/7, ein L3-Spieler sah auf 4 und
            // 5 ein unerklärtes "—".
            const unlockLevel = SLOT_UNLOCK[slot] ?? 1;
            const locked = player.level < unlockLevel;
            const cdMs = move ? (snap.player.cooldowns[move.id] ?? 0) : 0;
            const casting = move ? (playerCasting[move.id] ?? null) : null;
            const cdPct = !move ? 0 : Math.min(100, (cdMs / move.cooldownMs) * 100);
            const label = move
              ? moveDisplayName(move)
              : locked
                ? `Lv ${unlockLevel}`
                : "—";
            // Erklärung für JEDE Kachel — auch für gesperrte und leere. Genau
            // die brauchen sie, und genau die blieben vorher stumm (der
            // Radix-Trigger hing an einem `disabled` Button, und leere Slots
            // bekamen gar keinen Tooltip).
            const tip = move ? (
              <div className="text-left">
                <div className="font-semibold">{moveDisplayName(move)}</div>
                <div className="mt-1 text-[10px] opacity-90">
                  Slot {slot}
                  {slot === 1 && " · Slot-1-Priorität (auto)"}
                  {move.isUltimate && " · Ultimate"}
                </div>
                {/* Statt des rohen englischen Typs („· void") der Satz, der
                    wirklich zählt — mit der Prozentzahl aus dem Faktor, nie
                    getippt. */}
                <div className="text-[10px] opacity-90">
                  {typVorteilSatz(
                    vorteilFuer(move),
                    (activeEnemyFull?.type as CreatureType | undefined) ?? null,
                  )}
                </div>
                {/* Nur EINE Zeitangabe: der Zyklus. „Cast-Zeit X · CD Y"
                    nebeneinander lud dazu ein, beide zu addieren — bei kurzen
                    Cooldowns ist die Ausholzeit dort aber schon abgezogen. */}
                <div className="text-[10px] opacity-90">Power ×{de(move.power, 2)}</div>
                <div className="text-[10px] opacity-90">{castCycleText(move)}</div>
                {appliedStatuses.map(({ apply, label }) => (
                  <div key={apply.kind} className="text-[10px] opacity-90">
                    Effekt: {label.name} ({Math.round(apply.chance * 100)} % ·{" "}
                    {de(apply.durationMs / 1000, 1)}s)
                  </div>
                ))}
                {move.description && (
                  <div className="mt-1 border-t border-white/10 pt-1 text-[10px] opacity-80">
                    {move.description}
                  </div>
                )}
              </div>
            ) : locked ? (
              <div className="text-left">
                <div className="font-semibold">Slot {slot} · gesperrt</div>
                <div className="mt-1 text-[10px] opacity-90">
                  Öffnet sich mit Level {unlockLevel}. Danach kannst du hier im Attacken-Menü einen
                  gelernten Move ablegen.
                </div>
              </div>
            ) : (
              <div className="text-left">
                <div className="font-semibold">Slot {slot} · leer</div>
                <div className="mt-1 text-[10px] opacity-90">
                  Frei — lege im Attacken-Menü einen gelernten Move hierher.
                </div>
              </div>
            );
            return (
              <InfoTip key={slot} text={tip}>
                <MoveTile
                  kind={slot === MAX_MOVE_SLOTS ? "ultimate" : "slot"}
                  slot={slot}
                  vorteil={move ? vorteilFuer(move) : null}
                  name={move ? label : null}
                  cooldownPct={cdPct}
                  cast={
                    casting
                      ? {
                          pct: castProgressPct(casting),
                          restText: `${de(casting.remainingMs / 1000, 1)}s`,
                        }
                      : null
                  }
                  locked={locked}
                  unlockLevel={unlockLevel}
                  disabled={!move || locked || cdMs > 0 || kanalBelegt || snap.done}
                  onClick={() => move && live.cast(move.id)}
                />
              </InfoTip>
            );
          })}
        </div>
      </div>


      {/* Kampf-Details (Schalter, Aktiv-Bonus, Log). Auf dem Handy liegen sie
          hinter dem Bottom-Sheet — nur so passen Gegner, eigenes Xenomon und
          Slot-Leiste auf 390×844 ohne Scrollen (Design-Vorgabe). Ab `sm`
          stehen sie wie bisher direkt in der Seite. Der Block wird genau
          EINMAL gebaut und je nach Breite hier oder im Sheet eingehängt;
          zweimal gerendert (einmal per CSS versteckt) hinge der Log mit
          seinen 10 Aktualisierungen pro Sekunde doppelt im DOM. */}
      {isMobile ? null : kampfDetails}


      {snap.done && live.result && (() => {
        // Dieselbe Funktion, die auch der Settle benutzt — sonst sagt die
        // Anzeige etwas anderes als die Abrechnung.
        const zeitAbgelaufen = warZeitablauf(
          live.result.log,
          live.result.outcome,
          snap.player.id,
        );
        return (
        <div ref={ergebnisRef}>
        <Panel tone={snap.outcome === "win" ? "primary" : "enemy"} grid>
          {/* Der große Moment bekommt einen Sticker statt einer fetten Zeile —
              Gold für den Sieg, Rot für die Niederlage.

              ⚠️ Und seit dem 10.09.2026 einen dritten Fall: Läuft der Kampf
              in die Zeitgrenze, steht das Xenomon noch — das ist keine
              Niederlage und darf nicht so aussehen. Vorher bekam der Spieler
              denselben roten Sticker, mit fast voller Lebensleiste, und
              erfuhr mit keinem Wort, warum er verloren hatte. */}
          <div className="mb-2 text-center">
            <Sticker
              color={
                snap.outcome === "win"
                  ? "var(--gold)"
                  : zeitAbgelaufen
                    ? "var(--muted-foreground)"
                    : "var(--destructive)"
              }
              size={38}
              rotate={snap.outcome === "win" ? -4 : 3}
              className={snap.outcome === "win" ? undefined : "text-foreground"}
            >
              {snap.outcome === "win"
                ? "Sieg!"
                : zeitAbgelaufen
                  ? "Zeit abgelaufen"
                  : "Verloren"}
            </Sticker>
          </div>
          <div className="text-center text-xs text-muted-foreground">
            {de(snap.now / 1000, 1)}s · {snap.enemies.filter((e) => e.hp <= 0).length}/
            {snap.enemies.length} Kills
            {import.meta.env.DEV && ` · hash ${live.result.endStateHash}`}
          </div>
          {/* Die Belohnungen als Kacheln: XP gold, Gold gelb. Als Fließtext
              hat sie der Spieler bisher überlesen. */}
          {settleState.status === "done" && (
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 rounded-md bg-ink px-2.5 py-2 text-sm font-extrabold">
                <Sparkles className="h-4 w-4 shrink-0 text-xp" strokeWidth={2.5} />
                +{settleState.xp} XP
              </div>
              <div className="flex items-center gap-2 rounded-md bg-ink px-2.5 py-2 text-sm font-extrabold">
                <Coins className="h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                +{settleState.gold} Gold
              </div>
            </div>
          )}
          <div className="mt-2 text-[11px] text-muted-foreground">
            {settleState.status === "pending" && "Server-Abrechnung läuft…"}
            {settleState.status === "done" &&
              `Bestätigt · Aktiv-Bonus +${de(settleState.activeBonusPct * 100, 1)} %${settleState.isFrontier ? "" : " · Training: XP und Gold zählen 50 %"}`}
            {settleState.status === "error" && `Fehler: ${settleState.message}`}
          </div>
          {/* Was ist gefallen — und ist es besser als das Angelegte? Die Zahl
              „N Beute" allein hat der Spieler bisher übersehen. */}
          {settleState.status === "done" && (
            <LootReveal
              itemsCreated={settleState.itemsCreated}
              autoSalvaged={settleState.autoSalvaged}
              autoSalvageGold={settleState.autoSalvageGold}
              knownItemIds={preSettleItemIds.current}
              onUpgradeAvailable={setLootHold}
            />
          )}
          {settleState.status === "done" && lootHold && autoAdvance && (
            <div className="mt-2 text-[11px] text-amber-200">
              Auto-Weiter pausiert — du hast einen besseren Fund dabei.
            </div>
          )}
          {settleState.status === "done" && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {settleState.isFrontier ? (
                settleState.outcome === "win" ? (
                  <Button size="sm" onClick={manualAdvance} disabled={advancingRef.current}>
                    {countdown !== null ? `Weiter → (${countdown}s)` : "Weiter →"}
                  </Button>
                ) : (
                  <Button size="sm" onClick={manualAdvance} disabled={advancingRef.current}>
                    Erneut versuchen
                  </Button>
                )
              ) : settleState.outcome === "win" ? (
                <>
                  <Button size="sm" onClick={manualAdvance} disabled={advancingRef.current}>
                    {countdown !== null ? `Weiter → (${countdown}s)` : "Weiter →"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-sky-400/60 text-sky-100 hover:bg-sky-500/20"
                    onClick={() => {
                      onJumpToFrontier();
                      onExit();
                    }}
                  >
                    Zur Frontier →
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" onClick={manualAdvance} disabled={advancingRef.current}>
                    Erneut versuchen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-sky-400/60 text-sky-100 hover:bg-sky-500/20"
                    onClick={() => {
                      onJumpToFrontier();
                      onExit();
                    }}
                  >
                    Zur Frontier →
                  </Button>
                </>
              )}
              {countdown !== null && (
                <Button size="sm" variant="outline" onClick={cancelAutoAdvance}>
                  Auto-Weiter stoppen
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onExit}>
                Zurück
              </Button>
            </div>
          )}
          {settleState.status === "error" && (
            <>
              {/* „settle mismatch" ist fuer einen Spieler kein Satz. Der
                  haeufigste Grund dafuer ist ein veralteter Stand im
                  Browser — und der ist an dieser Stelle bereits
                  aufgefrischt (siehe .catch im Settle), ein neuer Versuch
                  laeuft also mit den richtigen Werten. */}
              {settleState.message.includes("settle mismatch") && (
                <div className="mt-2 rounded-md border-2 border-ink bg-amber-500/15 p-2 text-[11px] leading-relaxed">
                  Dein Browser hatte einen veralteten Stand deines Xenomons —
                  deshalb kam der Kampf hier anders aus als auf dem Server, und
                  er konnte nicht abgerechnet werden. Der Stand ist jetzt
                  aufgefrischt. Starte den Kampf noch einmal.
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={onExit}>
                  Zurück
                </Button>
              </div>
            </>
          )}
        </Panel>
        </div>
        );
      })()}

      {/* Bottom-Sheet „Kampf-Details" — nur auf dem Handy. Es hält denselben
          Block, der auf dem Desktop in der Seite steht: keine Funktion fehlt,
          sie liegt nur eine Berührung tiefer. */}
      {isMobile && (
        <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[85svh] overflow-y-auto rounded-t-xl border-t-2 border-ink"
          >
            <SheetHeader className="text-left">
              <SheetTitle>Kampf-Details</SheetTitle>
            </SheetHeader>
            <div className="mt-3 flex flex-col gap-3">{kampfDetails}</div>
          </SheetContent>
        </Sheet>
      )}

      {/* Ein Modal für alle Level dieses Kampfes — inkl. der Entscheidung,
          wenn alle freigeschalteten Slots belegt sind. */}
      {learnResult && (
        <MoveLearnedModal
          open
          learned={learnResult.learned}
          offerOpen={learnResult.offerOpen}
          onClose={() => setLearnResult(null)}
        />
      )}
    </div>
  );
}

function ExpeditionReadyBanner() {
  const { t } = useTranslation("game");
  const fetchActive = useServerFn(getActiveExpedition);
  const q = useQuery({
    queryKey: ["expedition-active"],
    queryFn: () => fetchActive(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
  const exp = q.data;
  if (!exp) return null;
  const ready = new Date(exp.ends_at).getTime() <= Date.now();
  if (!ready) return null;
  return (
    // Sekundär, nicht orange: Der eine Aktionsknopf dieser Ansicht ist
    // „Stage starten". Der Hinweis führt trotzdem als voller Knopf mit
    // Ink-Outline und Hartschatten, damit er als antippbar gelesen wird.
    <Button asChild variant="secondary" className="w-full justify-start gap-2">
      <Link to="/game/expeditions">
        <Sparkles className="h-4 w-4" strokeWidth={2.5} aria-hidden />
        {t("exp.battleBanner")}
      </Link>
    </Button>
  );
}

